import { useEffect, useMemo, useRef, useState } from "react";
import {
  X, Loader2, Check, AlertTriangle, Download, Mail, PenTool, Clock, Users,
  Send, ChevronRight, ChevronDown, ChevronUp, Undo2, CheckCircle2, MinusCircle, XCircle,
  Thermometer, Ruler, ImageIcon, ZoomIn, ClipboardCheck, Trash2, Zap, ExternalLink,
} from "lucide-react";
import { api, apiError, API_BASE } from "@/lib/api";
import { downloadReportPdf, reportPdfBase64 } from "@/lib/reportPdf";
import { signatureSrc } from "@/lib/signatures";
import { fetchClosedInspectionSet } from "@/lib/swingLookup";
import EmailModal from "@/components/EmailModal";
import SignaturePad from "@/components/SignaturePad";
import SignoffCommandCentre from "@/components/SignoffCommandCentre";
import BucketWearGrid from "@/components/BucketWearGrid";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";

/**
 * InspectionDetail — pixel-perfect clone of the mobile app inspection view.
 *
 * Layout (top → bottom):
 *   • Header: HUGE asset_id in Safety Orange, template name subtitle.
 *   • Stats row: OK · ISSUE · N/A · PPF · OPEN (colour-coded counts).
 *   • Sections. Each section:
 *       - "SECTION NAME" in orange caps
 *       - optional intro text
 *       - optional ref image with "TAP TO MARK UP" button
 *       - task cards, one per item; each shows the task label, the audit
 *         trail ("Control · 4 Jul 2:16 pm · OK"), and inputs specific to
 *         the item kind (check / measurement / pin_matrix / selector).
 *   • Bottom: big "SUBMIT FOR SIGN-OFF" orange button (tradesperson) OR
 *     "Approve" + "Send Back" pair (supervisor).
 *
 * All writes go to production Pit Link:
 *   PATCH /api/inspections/{iid}                              — meta edits
 *   PATCH /api/inspections/{iid}/items/{item_id}              — task state
 *   POST  /api/inspections/{iid}/ready                        — tradesperson submits
 *   POST  /api/inspections/{iid}/approve                      — supervisor signs off
 *   POST  /api/inspections/{iid}/send-back  {reason}          — supervisor rejects
 */
export default function InspectionDetail({ inspectionId, initial, onClose, onUpdated, mode = "drawer" }) {
  const { user } = useAuth();
  const [insp, setInsp] = useState(initial || null);
  const [loading, setLoading] = useState(!initial);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [signature, setSignature] = useState(null);
  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [sendBackReason, setSendBackReason] = useState("");
  const [lightbox, setLightbox] = useState(null); // photo url
  const [savingItems, setSavingItems] = useState(() => new Set());
  // Sign-off summary panel — collapsed by default so the inspection reads
  // like the mobile app (Josh Feb 23: "just make it simple, easier to scroll
  // through to look at the work done"). Supervisors expand it before signing.
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const savingRef = useRef(savingItems);
  savingRef.current = savingItems;
  // Top-level field writes in flight — used to shield optimistic edits
  // (e.g. work_order_number typed into the header chip) from being clobbered
  // by the 3s poll before the server has committed the PATCH. See mergeInsp.
  const savingFieldsRef = useRef(new Set());
  // Body scroll ref — used to force-scroll the drawer back to the top on
  // open so the ReviewSummary panel is the first thing the supervisor sees
  // (instead of the drawer opening at the signature pad on some devices).
  const bodyRef = useRef(null);
  const signaturePanelRef = useRef(null);

  // Hard refetch from server — used after every write to lock the UI onto
  // server truth so the portal never drifts from what the mobile app sees.
  // Preserves any items OR top-level fields that are STILL in-flight so
  // their optimistic edits aren't clobbered mid-save.
  const mergeInspFromServer = (data, prev) => {
    if (!prev) return data;
    const merged = { ...data, items_state: { ...(data.items_state || {}) } };
    // Preserve in-flight item PATCHes (teeth/pin-matrix/status).
    savingRef.current.forEach((id) => {
      if (prev.items_state?.[id]) merged.items_state[id] = prev.items_state[id];
    });
    // Preserve in-flight top-level PATCHes (WO#, SMU via item, etc.). Without
    // this the poll wins the race and the header chip flips back to blank
    // for the ~1-2s between local optimistic set and the server committing.
    savingFieldsRef.current.forEach((field) => {
      if (field in prev) merged[field] = prev[field];
    });
    return merged;
  };

  const refetch = async () => {
    if (!inspectionId) return;
    try {
      const { data } = await api.get(`/inspections/${inspectionId}`);
      setInsp((prev) => mergeInspFromServer(data, prev));
    } catch { /* silent — the 5s poll will retry */ }
  };

  useEffect(() => {
    (async () => {
      if (!inspectionId) return;
      try {
        setLoading(true);
        const { data } = await api.get(`/inspections/${inspectionId}`);
        setInsp(data);
      } catch (ex) {
        setMsg({ type: "err", text: apiError(ex) });
      } finally { setLoading(false); }
    })();
  }, [inspectionId]);

  // Whenever a new inspection loads, jump the drawer body back to the very
  // top so the ReviewSummary panel is on-screen. Without this some devices
  // open the drawer already scrolled to the signature pad (browser tries to
  // scroll the sign-off canvas into view because it's the tallest child).
  useEffect(() => {
    if (loading) return;
    const el = bodyRef.current;
    if (!el) return;
    // Two rAF's so the layout has settled after ReviewSummary renders.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => { el.scrollTop = 0; });
    });
  }, [inspectionId, loading]);

  // Live sync — while this inspection is open, poll every 3s so any teeth-%,
  // pin-matrix or status change made in the mobile app (or by another crew
  // member) appears within seconds. We DO NOT skip the poll while items are
  // saving; instead, the merge below preserves the in-flight items so their
  // optimistic UI isn't clobbered while the server catches up.
  useEffect(() => {
    if (!inspectionId) return;
    const iv = setInterval(async () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      try {
        const { data } = await api.get(`/inspections/${inspectionId}`);
        setInsp((prev) => mergeInspFromServer(data, prev));
      } catch {
        /* silent — next tick will retry */
      }
    }, 3000);
    // Extra: refetch immediately when the tab becomes visible so switching
    // mobile ↔ portal shows fresh data without waiting for the next 5s tick.
    const onVis = () => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        refetch();
      }
    };
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(iv);
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVis);
    };
  }, [inspectionId]);

  const itemsState = insp?.items_state || {};
  const allItems = useMemo(() => flattenItems(insp), [insp]);

  const stats = useMemo(() => {
    const c = {
      ok: 0, issue: 0, na: 0, ppf: 0, open: 0,
      newp: 0, w25: 0, w50: 0, w75: 0,
      total: allItems.length,
    };
    allItems.forEach((it) => {
      const st = itemsState[it.id]?.status;
      const stStr = String(st ?? "").toLowerCase();
      if (it.kind === "wear_pct") {
        if (stStr === "new" || st === 0 || st === "0") c.newp++;
        else if (String(st) === "25") c.w25++;
        else if (String(st) === "50") c.w50++;
        else if (String(st) === "75") c.w75++;
        else if (String(st) === "100") c.issue++; // change-out treated as issue-tier
        else c.open++;
        return;
      }
      if (stStr === "ok") c.ok++;
      else if (stStr === "issue") { c.issue++; if (itemsState[it.id]?.action_priority) c.ppf++; }
      else if (stStr === "na") c.na++;
      else c.open++;
    });
    return c;
    // itemsState is a fresh object every render from `insp?.items_state` —
    // adding it to the deps would re-run this memo on every parent render.
    // The `insp` identity change (via refetch) is what we actually want to
    // react to, and that already propagates through `allItems`.
  }, [allItems, insp]);

  const isSupervisor = can(user, "sendBackInspection");
  const status = String(insp?.status || "").toLowerCase();
  const isFinalisedByStatus = ["approved", "closed", "complete", "signed"].some((k) => status.includes(k));
  const isSubmitted = status.includes("submit") || status.includes("ready") || status.includes("pending");

  // Josh (Jul 10) locked the rule: once the parent swing is CLOSED, every
  // inspection that was part of it becomes strictly view-only — no more
  // status changes, no work-order/SMU edits, no sign-offs, no delete. This
  // catches the case a supervisor drills back into a past swing to check
  // "we only got to 90 %" without accidentally re-opening the shift.
  //
  // Populated by a shared 10 s-cached fetch of /swings — safe to call from
  // multiple drawers concurrently (module-level in-flight de-dup).
  const [parentSwingClosed, setParentSwingClosed] = useState(false);
  useEffect(() => {
    if (!inspectionId) return undefined;
    let alive = true;
    fetchClosedInspectionSet().then((set) => {
      if (alive) setParentSwingClosed(set.has(String(inspectionId)));
    });
    return () => { alive = false; };
  }, [inspectionId]);

  const isFinalised = isFinalisedByStatus || parentSwingClosed;

  // SMU (Service Meter Unit) — the top-most `measurement` item labelled
  // "…S.M.U.…" in the inspection template. Entering hours in the sticky
  // header PATCHes this item's value so the mobile app's SMU field, the
  // Sign-off Command Centre, and the compliance PDF all agree on the
  // recorded hours.
  const smuItem = useMemo(() => {
    if (!allItems.length) return null;
    return allItems.find((it) => it.kind === "measurement" && /s\.?m\.?u|meter/i.test(it.label || "")) || null;
  }, [allItems]);
  const smuValue = smuItem ? (itemsState[smuItem.id]?.value ?? "") : "";

  // Work Order # is stored as a top-level `work_order_number` field on the
  // inspection (verified against prod). Editable anytime up to sign-off.
  const workOrderNumber = insp?.work_order_number || "";
  const saveWorkOrder = async (next) => {
    if (!inspectionId) return;
    const clean = String(next || "").trim();
    if (clean === String(workOrderNumber || "").trim()) return;
    // Optimistic
    setInsp((p) => ({ ...(p || {}), work_order_number: clean || null }));
    // Shield this field from the 3s poll's overwrite until the PATCH commits.
    savingFieldsRef.current.add("work_order_number");
    try {
      await api.patch(`/inspections/${inspectionId}`, { work_order_number: clean || null });
      onUpdated?.();
    } catch (ex) {
      setMsg({ type: "err", text: apiError(ex) });
      // Roll back
      setInsp((p) => ({ ...(p || {}), work_order_number: workOrderNumber || null }));
    } finally {
      // Give the server a beat to make the write visible to subsequent GETs
      // (avoids the race where the poll fires between our PATCH resolving
      // and the read replica catching up).
      setTimeout(() => savingFieldsRef.current.delete("work_order_number"), 1500);
    }
  };
  const saveSmuValue = async (next) => {
    if (!smuItem) return;
    // Guard against empty-string → 0 (Number('') === 0). The chip already
    // guards but the sign-off panel also calls this with a possibly-blank
    // draft; without this we'd silently PATCH SMU=0 when the user just
    // tabbed through the field.
    const raw = String(next ?? "").trim();
    if (raw === "") return;
    const num = Number(raw);
    if (!Number.isFinite(num)) return;
    await setItemValue(smuItem, num);
  };

  // Every wear_pct item on this inspection — used for the "Set ALL teeth"
  // bulk-apply row at the top of the body. Empty for check-only inspections.
  const wearItems = useMemo(() => allItems.filter((it) => it.kind === "wear_pct"), [allItems]);
  const [bulkBusy, setBulkBusy] = useState(false);

  // The mobile app writes wear to TWO endpoints when a crew taps a tooth:
  //   1. PATCH /inspections/{iid}/items/{item_id}   — per-item status
  //   2. PATCH /get-wear/{asset_id}                 — asset-level per-position
  //      map ({tooth:{"Tooth Pos 1":"25", …}, lip_shroud:{…}, wing_shroud:{…}})
  // The Shift Summary + mobile Asset overview read from /get-wear, so if the
  // portal only writes (1) the mobile app's asset view stays stale ("teeth
  // not syncing"). We mirror both writes here.
  //
  // Complication: different assets have DIFFERENT position labels in
  // /get-wear — some have `Lip Shroud Pos 1..5` (matches inspection), some
  // have `Lip Shroud Left/Centre/Right`, and some have `ALL LIP SHROUDS`.
  // To keep every consumer aligned we always write BOTH the inspection
  // labels AND every existing legacy key in the same category (set to the
  // worst-of the inspection positions for that category).
  const wearCategoryOf = (label) => {
    const L = String(label || "").toLowerCase();
    if (L.includes("wing")) return "wing_shroud";
    if (L.includes("lip")) return "lip_shroud";
    if (L.includes("tooth")) return "tooth";
    return null;
  };
  // "new"/"" → 0, "25" → 25 …
  const numFor = (token) => {
    const s = String(token ?? "").toLowerCase();
    if (s === "new" || s === "" || s === "0") return 0;
    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
  };
  const tokenFor = (n) => {
    if (n <= 0) return "new";
    return String(n);
  };
  // Given: items[] — every wear_pct item in the inspection; overrides — a
  // map of itemId → token for the write we just made (so we mirror what the
  // server WILL be, not what it currently is).
  //
  // CRITICAL: only include items with an EXPLICIT status (either in overrides
  // or a non-null itemsState). Untouched items MUST be preserved as-is on
  // /get-wear — writing them as "new" would silently reset the mobile app's
  // wear values every time the portal edits a single tooth (previous bug).
  const buildFullMirror = (items, overrides = {}, currentWear = null) => {
    const patch = {};
    ["tooth", "lip_shroud", "wing_shroud"].forEach((cat) => {
      const inspItems = items.filter((it) => wearCategoryOf(it.label) === cat);
      // Only items with an explicit new value (touched now OR previously)
      const touched = [];
      inspItems.forEach((it) => {
        const hasOverride = Object.prototype.hasOwnProperty.call(overrides, it.id);
        const rawStatus = itemsState[it.id]?.status;
        const hasExplicit = rawStatus != null && rawStatus !== "";
        if (!hasOverride && !hasExplicit) return;
        const raw = hasOverride ? overrides[it.id] : rawStatus;
        touched.push({ label: it.label, val: numFor(raw) });
      });
      if (touched.length === 0) return; // nothing to sync for this category
      const dict = {};
      // Write only the touched inspection labels with their per-item value.
      touched.forEach(({ label, val }) => { dict[label] = tokenFor(val); });
      // Compute worst-of factoring in touched values + untouched existing
      // per-position values + existing legacy keys — so legacy aggregates
      // stay honest even when only one position was clicked in the portal.
      const existingMap = (currentWear && currentWear[cat]) || {};
      const inspLabelSet = new Set(inspItems.map((it) => it.label));
      let worst = 0;
      Object.entries(existingMap).forEach(([label, val]) => {
        const isInspLabel = inspLabelSet.has(label);
        const touchedForLabel = touched.find((t) => t.label === label);
        const v = touchedForLabel ? touchedForLabel.val
                                  : (isInspLabel ? numFor(val) : numFor(val));
        if (v > worst) worst = v;
      });
      // Include touched values that don't yet have an existing key.
      touched.forEach(({ val }) => { if (val > worst) worst = val; });
      const worstTok = tokenFor(worst);
      // Update legacy keys (i.e. non-inspection labels like "Wing Shroud
      // Left", "ALL LIP SHROUDS") to the recomputed worst-of.
      Object.keys(existingMap).forEach((k) => {
        if (inspLabelSet.has(k)) return; // never overwrite an untouched inspection position
        if (dict[k] === undefined) dict[k] = worstTok;
      });
      patch[cat] = dict;
    });
    return patch;
  };
  const mirrorToGetWear = async (writtenItems, token) => {
    const assetId = insp?.asset_id;
    if (!assetId) return;
    if (!writtenItems || writtenItems.length === 0) return;
    // Build overrides so the mirror reflects the write we JUST made — the
    // server may not have re-read this asset's /get-wear yet.
    const overrides = {};
    writtenItems.forEach((it) => { overrides[it.id] = token; });
    // Fetch current /get-wear so we can also refresh every legacy key.
    let currentWear = null;
    try {
      const { data } = await api.get("/get-wear");
      const arr = Array.isArray(data) ? data : (data?.items || data?.results || []);
      currentWear = arr.find((r) => r.asset_id === assetId) || null;
    } catch { /* if we can't read /get-wear, we still send the inspection labels */ }
    const body = buildFullMirror(wearItems, overrides, currentWear);
    if (Object.keys(body).length === 0) return;
    try {
      await api.patch(`/get-wear/${assetId}`, {
        ...body,
        client_op_id: `wear-mirror-${assetId}-${Date.now()}`,
      });
    } catch (ex) {
      // Surface a soft warning — the inspection PATCH already succeeded;
      // /get-wear will reconcile on the next mobile-app write / adjust.
      console.warn("[wear-mirror] /get-wear PATCH failed:", ex?.response?.data || ex?.message);
    }
  };

  const applyToAllTeeth = async (token) => {
    return applyToWearItems(wearItems, token);
  };

  // Bulk-apply a wear token to an arbitrary subset of wear items — used
  // by BucketWearGrid's per-category "Set all" strip so a supervisor can
  // knock out all teeth (or all lip shrouds, or all wing shrouds) in a
  // single tap.
  const applyToWearItems = async (targetItems, token) => {
    if (isFinalised || bulkBusy || !targetItems || targetItems.length === 0) return;
    setBulkBusy(true);
    setMsg(null);
    const now = new Date().toISOString();
    const who = user?.name || user?.username || "";
    setInsp((p) => {
      const next = { ...(p.items_state || {}) };
      targetItems.forEach((it) => {
        next[it.id] = {
          ...(next[it.id] || {}),
          status: token,
          clicked_by: who,
          clicked_at: now,
        };
      });
      return { ...p, items_state: next };
    });
    setSavingItems((s) => {
      const n = new Set(s);
      targetItems.forEach((it) => n.add(it.id));
      return n;
    });
    try {
      // Server has a race condition when concurrent PATCHes hit the same
      // inspection (verified live: 6 parallel PATCHes → only 2 persist).
      // Send them SEQUENTIALLY so every write lands. Slightly slower but
      // 100% reliable.
      const results = [];
      for (const it of targetItems) {
        try {
          await api.patch(`/inspections/${inspectionId}/items/${it.id}`, {
            status: token,
            clicked_by: who,
            clicked_at: now,
            client_op_id: `${it.id}-${Date.now()}`,
            extras: { clicked_at: now, clicked_by: who, source: "portal-bulk" },
          });
          results.push({ ok: true });
        } catch (ex) {
          results.push({ ok: false, ex });
        }
      }
      const failed = results.filter((r) => !r.ok);
      if (failed.length > 0) {
        setMsg({ type: "err", text: `${failed.length} of ${targetItems.length} positions failed to save.` });
      }
      await mirrorToGetWear(targetItems, token);
      onUpdated?.();
    } finally {
      setSavingItems((s) => {
        const n = new Set(s);
        targetItems.forEach((it) => n.delete(it.id));
        return n;
      });
      setBulkBusy(false);
      await refetch();
    }
  };

  const setItemStatus = async (item, newStatus) => {
    if (isFinalised) return;
    const now = new Date().toISOString();
    const who = user?.name || user?.username || "";
    const prev = itemsState[item.id] || {};
    // Optimistic UI + saving flag
    setSavingItems((s) => new Set(s).add(item.id));
    setInsp((p) => ({
      ...p,
      items_state: {
        ...(p.items_state || {}),
        [item.id]: {
          ...prev,
          status: newStatus,
          clicked_by: who,
          clicked_at: now,
        },
      },
    }));
    try {
      await api.patch(`/inspections/${inspectionId}/items/${item.id}`, {
        // Mirror the mobile-app storage shape: `status` + top-level
        // `clicked_by` / `clicked_at`. `extras` is still sent as a fallback for
        // servers that store it there instead.
        status: newStatus,
        clicked_by: who,
        clicked_at: now,
        client_op_id: `${item.id}-${Date.now()}`,
        extras: { clicked_at: now, clicked_by: who, source: "portal" },
      });
      // If this is a wear_pct tooth/lip/wing item, mirror the change to the
      // asset-level /get-wear table so the mobile app sees it too.
      if (item.kind === "wear_pct") {
        await mirrorToGetWear([item], newStatus);
      }
      onUpdated?.();
    } catch (ex) {
      // Roll back on failure so the UI reflects the truth
      setInsp((p) => ({
        ...p,
        items_state: { ...(p.items_state || {}), [item.id]: prev },
      }));
      setMsg({ type: "err", text: apiError(ex) });
    } finally {
      setSavingItems((s) => { const n = new Set(s); n.delete(item.id); return n; });
      // Refetch so the local UI reads exactly what the server just persisted.
      // Prevents drift where the mobile app reads a different value than the
      // portal was optimistically showing.
      await refetch();
    }
  };

  const setItemValue = async (item, value) => {
    if (isFinalised) return;
    const now = new Date().toISOString();
    const who = user?.name || user?.username || "";
    const prev = itemsState[item.id] || {};
    try {
      setInsp((p) => ({
        ...p,
        items_state: {
          ...(p.items_state || {}),
          [item.id]: { ...prev, value, clicked_by: who, clicked_at: now },
        },
      }));
      // Shield this item from being overwritten by the 3s poll's server
      // truth until the PATCH commits + the read replica catches up.
      setSavingItems((s) => new Set(s).add(item.id));
      await api.patch(`/inspections/${inspectionId}/items/${item.id}`, {
        value: Number(value) || value,
        client_op_id: `${item.id}-val-${Date.now()}`,
        extras: { clicked_at: now, clicked_by: who, source: "portal" },
      });
      onUpdated?.();
    } catch (ex) {
      setMsg({ type: "err", text: apiError(ex) });
    } finally {
      // Small buffer so the next GET reflects our write.
      setTimeout(() => {
        setSavingItems((s) => { const n = new Set(s); n.delete(item.id); return n; });
      }, 1500);
    }
  };

  const setItemExtras = async (item, extras) => {
    if (isFinalised) return;
    const now = new Date().toISOString();
    const who = user?.name || user?.username || "";
    const prev = itemsState[item.id] || {};
    // The caller ships { value?, extras: {pins, avg, ...} } — flatten so the
    // inner keys (pins, track_id, avg, counts…) sit at the top of state
    // where PinMatrix's useEffect actually reads them.
    const nested = extras?.extras || {};
    const flat = { ...extras };
    delete flat.extras;
    try {
      setInsp((p) => ({
        ...p,
        items_state: {
          ...(p.items_state || {}),
          [item.id]: {
            ...prev,
            ...flat,
            ...nested,
            clicked_by: who,
            clicked_at: now,
          },
        },
      }));
      await api.patch(`/inspections/${inspectionId}/items/${item.id}`, {
        ...flat,
        client_op_id: `${item.id}-extra-${Date.now()}`,
        extras: { ...nested, clicked_at: now, clicked_by: who, source: "portal" },
      });
      onUpdated?.();
    } catch (ex) { setMsg({ type: "err", text: apiError(ex) }); }
  };

  const submitForSignoff = async () => {
    if (!user?.id) return;
    setBusy(true); setMsg(null);
    try {
      await api.post(`/inspections/${inspectionId}/ready`, {
        supervisor_id: user.id,
        tradesperson_sig: signature || "",
        overall_comment: "",
        force: false,
      });
      setMsg({ type: "ok", text: "Submitted for sign-off." });
      onUpdated?.();
    } catch (ex) {
      setMsg({ type: "err", text: apiError(ex) });
    } finally { setBusy(false); }
  };

  const [signOffSmu, setSignOffSmu] = useState("");
  const [signOffWO, setSignOffWO] = useState("");

  // Keep the sign-off panel inputs at the bottom of the drawer in sync with
  // the WO# / SMU chips at the top. When a supervisor types SMU/WO in either
  // place, both fields — and the underlying inspection record — reflect the
  // same value so it's never ambiguous which one the crew filled in.
  useEffect(() => {
    setSignOffSmu(smuValue !== "" && smuValue != null ? String(smuValue) : "");
  }, [smuValue]);
  useEffect(() => {
    setSignOffWO(workOrderNumber || "");
  }, [workOrderNumber]);

  // Persist the sign-off panel values back to the inspection on blur/Enter,
  // BEFORE the supervisor taps Approve. This means:
  //   1. If they type SMU/WO here first, the top chips update instantly.
  //   2. If they type at the top first, the bottom mirrors.
  //   3. Approving with values already saved means no last-second race
  //      writes on top of the approval POST.
  const commitSignOffSmu = async () => {
    const draft = String(signOffSmu ?? "").trim();
    const current = String(smuValue ?? "").trim();
    if (!draft || draft === current) return;
    await saveSmuValue(draft);
  };
  const commitSignOffWO = async () => {
    const draft = String(signOffWO ?? "").trim();
    const current = String(workOrderNumber ?? "").trim();
    if (draft === current) return;
    await saveWorkOrder(draft);
  };

  const approve = async () => {
    if (!signature) { setMsg({ type: "err", text: "Draw your signature first." }); return; }
    setBusy(true); setMsg(null);
    try {
      // Flush any un-blurred edits from the sign-off panel to the inspection
      // record FIRST so the WO# / SMU chips at the top and the compliance
      // PDF both reflect what the supervisor typed here (in case they tapped
      // Approve without moving focus off the input).
      await commitSignOffSmu();
      await commitSignOffWO();
      // The prod API accepts { supervisor_sig, supervisor_name, hours, notes }.
      // We map SMU → hours (numeric), and stash the WO# as a "WO: …" prefix on
      // notes so it survives round-trips and shows up on the compliance PDF.
      const hoursNum = signOffSmu ? Number(signOffSmu) : undefined;
      const woPart = signOffWO.trim() ? `WO: ${signOffWO.trim()}` : "";
      const noteBody = woPart || undefined;
      await api.post(`/inspections/${inspectionId}/approve`, {
        supervisor_sig: signature,
        supervisor_name: user?.name || user?.username || "",
        ...(Number.isFinite(hoursNum) ? { hours: hoursNum } : {}),
        ...(noteBody ? { notes: noteBody } : {}),
      });
      setMsg({ type: "ok", text: "Inspection approved." });
      // Pull the server's post-approval snapshot so the drawer shows the
      // locked / approved state before it closes. The mobile app will see the
      // same snapshot on its next poll.
      await refetch();
      onUpdated?.();
      setTimeout(onClose, 800);
    } catch (ex) {
      setMsg({ type: "err", text: apiError(ex) });
    } finally { setBusy(false); }
  };

  const sendBack = async () => {
    if (!sendBackReason.trim()) { setMsg({ type: "err", text: "Give the tradesperson a reason." }); return; }
    setBusy(true); setMsg(null);
    try {
      await api.post(`/inspections/${inspectionId}/send-back`, { reason: sendBackReason.trim() });
      setMsg({ type: "ok", text: "Sent back to tradesperson." });
      onUpdated?.();
      setTimeout(onClose, 800);
    } catch (ex) {
      setMsg({ type: "err", text: apiError(ex) });
    } finally { setBusy(false); }
  };

  const downloadPdf = () => { if (insp) downloadReportPdf(insp, "Inspection"); };

  // Josh (Feb 26): "Add a manual 'Send to Email' button on the inspection
  // completion/summary screen. When this button is clicked, the system must
  // generate and send the PDF to the email address of the currently
  // logged-in user." One-tap flow — no recipient picker, no modal, straight
  // to the signed-in user's own email. Auto-send from the backend must be
  // disabled separately (prod API) per Josh's spec.
  const sendPdfToMe = async () => {
    if (!insp || emailBusy) return;
    const me = String(user?.email || "").trim();
    if (!me) {
      setMsg({ type: "err", text: "No email on your profile — set one under Profile first." });
      return;
    }
    setEmailBusy(true);
    setMsg(null);
    try {
      const b64 = await reportPdfBase64(insp, "Inspection");
      const filename = `inspection-${String(insp.id || insp._id || "record").slice(0, 8)}.pdf`;
      const subject = `Inspection — ${insp.asset_id || insp.template_name || (insp.id || "").slice(0, 8)}`;
      const html = `<div style="font-family:Arial,sans-serif;font-size:14px;color:#111"><p>The inspection PDF you requested is attached.</p><p style="color:#666;font-size:12px">Sent from Pit Link Portal</p></div>`;
      await api.post("/email/send-document", {
        kind: "inspection",
        doc_id: insp.id || insp._id,
        to: me,
        recipient: me,
        email: me,
        subject,
        html,
        message: "",
        attach_pdf: true,
        pdf_filename: filename,
        asset_id: insp.asset_id,
        resource_type: "inspection",
        resource_id: insp.id || insp._id,
        attachments: [{ filename, content: b64, content_type: "application/pdf" }],
      });
      setMsg({ type: "ok", text: `PDF sent to ${me}` });
    } catch (ex) {
      const status = ex?.response?.status;
      const fallback = (status === 404 || status === 405)
        ? "Email service not enabled on this backend — download the PDF instead."
        : apiError(ex);
      setMsg({ type: "err", text: fallback });
    } finally {
      setEmailBusy(false);
    }
  };

  const deleteInspection = async () => {
    if (!insp?.id && !insp?._id) return;
    setDeleting(true);
    try {
      // Production backend only accepts DELETE on the admin-scoped route
      // (`/admin/inspections/{id}`); the plain `/inspections/{id}` DELETE
      // returns 405 Method Not Allowed. Server soft-archives the record so
      // history is preserved and it disappears from the live list.
      await api.delete(`/admin/inspections/${insp.id || insp._id}`);
      onClose?.();
    } catch (ex) {
      setMsg({ type: "err", text: apiError(ex) });
    } finally {
      setDeleting(false);
      setShowDelete(false);
    }
  };

  const isPageMode = mode === "page";
  return (
    <div
      className={isPageMode ? "min-h-screen w-full" : "fixed inset-0 z-50 flex"}
      data-testid="inspection-detail"
    >
      {!isPageMode && (
        <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }} onClick={onClose} />
      )}
      <aside
        className={
          isPageMode
            ? "w-full min-h-screen flex flex-col relative"
            : "ml-auto w-full max-w-6xl h-screen flex flex-col relative overflow-hidden"
        }
        style={{
          background: "var(--surface)",
          borderLeft: isPageMode ? "none" : "1px solid var(--border)",
        }}
      >
        {/* Header */}
        <div className="p-5 flex items-start gap-3 sticky top-0 z-20" style={{ borderBottom: "1px solid var(--border)", background: "var(--surface)" }}>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-muted">Inspection · #{String(insp?.id || "").slice(0, 8)}</div>
            <div
              className="font-display font-black tracking-tight leading-none mt-1 text-4xl md:text-5xl"
              style={{ color: "var(--primary)" }}
              data-testid="inspection-asset-id"
            >
              {insp?.asset_id || "Loading…"}
            </div>
            <div className="text-sm text-white/80 mt-1.5 truncate">
              {insp?.template_name || insp?.title || ""}
              {insp?.assigned_crew_name && <> · <span className="text-muted">{insp.assigned_crew_name}</span></>}
            </div>
            {/* Live-save status pill — mirrors the mobile app's "auto-save"
                behaviour so tradespeople on the portal have the same
                confidence as on-tablet: every OK/Issue/N/A tap, numeric
                entry, or photo capture PATCHes the shared DB instantly. */}
            <LiveSaveStatus
              isFinalised={isFinalised}
              isSubmitted={isSubmitted}
              savingCount={savingItems.size}
              readOnly={parentSwingClosed && !isFinalisedByStatus}
            />
            {parentSwingClosed && !isFinalisedByStatus && (
              <div
                className="mt-2 inline-flex items-center gap-1.5 pill !text-[10px] !py-1 !px-2 uppercase font-black tracking-widest"
                style={{
                  background: "rgba(148,163,184,0.14)",
                  color: "#cbd5e1",
                  border: "1px solid rgba(148,163,184,0.35)",
                }}
                data-testid="inspection-past-shift-banner"
                title="This inspection belongs to a swing that has already ended — view only."
              >
                <ClipboardCheck className="w-3 h-3" /> Past shift · View only
              </div>
            )}
            {/* Quick meta row — Work Order # + SMU (hrs). Both inline-editable
                any time before sign-off; both flow into the top of every PDF
                and the mobile-app inspection view via `work_order_number` /
                the SMU measurement item. */}
            {insp && (
              <div className="flex flex-wrap items-center gap-2 mt-2" data-testid="inspection-meta-chips">
                <WorkOrderChip
                  value={workOrderNumber}
                  onSave={saveWorkOrder}
                  disabled={isFinalised}
                />
                {smuItem && (
                  <SmuChip
                    value={smuValue}
                    unit={smuItem.unit || "hrs"}
                    onSave={saveSmuValue}
                    disabled={isFinalised}
                  />
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {isSubmitted && !isFinalised && isSupervisor && (
              <button
                className="btn btn-sm btn-primary"
                onClick={() => {
                  const target =
                    bodyRef.current?.querySelector('[data-testid="signature-panel"]') ||
                    bodyRef.current?.querySelector('[data-testid="signoff-details-panel"]');
                  if (target && bodyRef.current) {
                    bodyRef.current.scrollTo({
                      top: target.offsetTop - 20,
                      behavior: "smooth",
                    });
                  }
                }}
                data-testid="inspection-jump-to-sign"
                title="Jump straight to the signature pad"
                style={{ fontWeight: 900, letterSpacing: "0.06em" }}
              >
                <PenTool className="w-4 h-4" /> SIGN NOW
              </button>
            )}
            <button className="btn btn-sm btn-ghost" onClick={downloadPdf} title="Download PDF" data-testid="inspection-download">
              <Download className="w-4 h-4" />
            </button>
            {/* Josh (Feb 26): one-tap send-to-me. Sends the PDF straight to
                the signed-in user's own email — no modal, no recipient
                picker. Replaces the modal-based email icon so there's a
                clear, deterministic path from "I want this in my inbox"
                to a sent email. */}
            <button
              className="btn btn-sm"
              onClick={sendPdfToMe}
              disabled={emailBusy || !insp}
              title={user?.email ? `Send PDF to ${user.email}` : "Set an email on your Profile to enable this"}
              data-testid="inspection-send-to-me"
              style={{
                background: "rgba(245,130,32,0.14)",
                border: "1px solid rgba(245,130,32,0.4)",
                color: "var(--primary)",
              }}
            >
              {emailBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              <span className="hidden md:inline">Send to Email</span>
            </button>
            {/* Retained "Email someone else" flow behind the small ⋯ affordance
                — keeps the primary CTA clean while still supporting
                supervisors who need to forward to a specific address. */}
            <button
              className="btn btn-sm btn-ghost !px-2"
              onClick={() => setEmailOpen(true)}
              title="Email to a different address"
              data-testid="inspection-email-other"
            >
              <Send className="w-4 h-4" />
            </button>
            {canDeleteInspection(user, insp) && !parentSwingClosed && (
              <button
                className="btn btn-sm btn-ghost"
                onClick={() => setShowDelete(true)}
                title="Delete this inspection"
                data-testid="inspection-delete"
                style={{ color: "#ff9b9b" }}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            <button className="btn btn-sm btn-ghost" onClick={onClose} data-testid="inspection-close">
              <X className="w-4 h-4" /> {isPageMode ? "Exit" : "Close"}
            </button>
          </div>
        </div>

        {/* Floating "Jump to Sign-off" pill — always in reach on the right
            edge of the viewport whenever there's a signature action waiting.
            Josh Feb 23: "also have a quick jump to button to quickly sign off". */}
        {!loading && insp && (isSubmitted || !isFinalised) && (
          <button
            type="button"
            onClick={() => signaturePanelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })}
            className="hidden md:flex fixed z-30 items-center gap-2 px-4 py-2.5 rounded-full font-black uppercase tracking-widest text-[11px] shadow-2xl"
            style={{
              right: 24,
              bottom: 24,
              background: "linear-gradient(180deg, var(--primary), #d97706)",
              color: "#0a0a0a",
              boxShadow: "0 14px 34px -8px rgba(245,130,32,0.7), 0 0 0 3px rgba(245,130,32,0.15)",
              border: "1px solid rgba(255,255,255,0.15)",
            }}
            data-testid="inspection-jump-to-signoff"
            title="Scroll straight to the sign-off panel"
          >
            <PenTool className="w-4 h-4" strokeWidth={2.5} />
            {isSubmitted && isSupervisor ? "Sign Off Now" : isFinalised ? "Signatures" : "Submit for Sign-off"}
          </button>
        )}

        {/* Stats row removed 2026-02-23 (Josh: "top is extra big and has
            everything double"). The SignoffCommandCentre inside the body
            already surfaces OK / ISSUE / N/A / PPF and the wear breakdown as
            its first block, so the outer strip was redundant + stole ~90px
            of permanent vertical space. The drawer now scrolls cleanly like
            the mobile app. */}

        {/* Body */}
        <div
          className={`p-5 pb-40 space-y-6 ${isPageMode ? "" : "flex-1 min-h-0 overflow-y-auto"}`}
          data-testid="inspection-body"
          ref={(el) => { bodyRef.current = el; }}
        >
          {/* Sign-off command centre — cinematic mission-control panel with
              radial progress ring, wear map, issues rail, and per-section
              rings. Collapsible so Josh can hide it and just scroll the raw
              checklist (mobile-parity) when he only wants the work-done view. */}
          {!loading && insp && (
            <div data-testid="inspection-summary-wrap">
              <button
                type="button"
                onClick={() => setSummaryOpen((v) => !v)}
                className="w-full flex items-center gap-2 mb-2 px-3 py-1.5 rounded-md text-[11px] uppercase tracking-widest font-black"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid var(--border)", color: "var(--muted)" }}
                data-testid="inspection-summary-toggle"
                title={summaryOpen ? "Hide the summary and show only the checklist" : "Show the sign-off summary"}
              >
                {summaryOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                {summaryOpen ? "Hide summary" : "Show summary"}
              </button>
              {summaryOpen && (
                <SignoffCommandCentre insp={insp} allItems={allItems} itemsState={itemsState} />
              )}
            </div>
          )}

          {/* Bulk-set teeth wear — a single tap sets every wear_pct item to
              the picked value. Only shown on digger-style inspections that
              actually have wear_pct items. */}
          {wearItems.length > 0 && !isFinalised && (
            <div
              className="panel-flat p-3 md:p-4 flex flex-wrap items-center gap-3"
              data-testid="bulk-wear-bar"
            >
              <div className="flex-1 min-w-[220px]">
                <div className="text-[10px] uppercase tracking-widest text-muted">Bulk apply</div>
                <div className="font-display font-black text-sm md:text-base leading-tight mt-0.5">
                  Set all {wearItems.length} teeth to
                </div>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="bulk-wear-buttons">
                {[
                  { key: "new", label: "NEW",     bg: "#0a2a12", border: "#4ade80", color: "#4ade80" },
                  { key: "25",  label: "25% WORN", bg: "#0a2a12", border: "#1e6b32", color: "#4ade80" },
                  { key: "50",  label: "50% WORN", bg: "#3a2f0d", border: "#c9a227", color: "#facc15" },
                  { key: "75",  label: "75% WORN", bg: "#4a2a06", border: "#f58220", color: "#ffb347" },
                ].map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => applyToAllTeeth(o.key)}
                    disabled={bulkBusy || isFinalised}
                    data-testid={`bulk-wear-${o.key}`}
                    className="px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest tabular-nums transition-transform hover:-translate-y-0.5"
                    style={{
                      background: o.bg,
                      border: `1px solid ${o.border}`,
                      color: o.color,
                      opacity: bulkBusy ? 0.6 : 1,
                    }}
                  >
                    {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : o.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {loading && (
            <div className="panel-flat p-10 flex items-center justify-center gap-3 text-muted">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading inspection…
            </div>
          )}
          {!loading && insp && (insp.sections || []).map((sec, si) => (
            <SectionBlock
              key={sec.id || si}
              section={sec}
              itemsState={itemsState}
              onStatus={setItemStatus}
              onValue={setItemValue}
              onExtras={setItemExtras}
              onOpenPhoto={setLightbox}
              savingItems={savingItems}
              disabled={isFinalised}
              onApplyToAllWear={applyToWearItems}
              inspection={insp}
            />
          ))}

          {/* Signatures captured on the mobile app render here so supervisors
              (and the tradesperson themselves) can verify what was signed. */}
          {!loading && insp && (insp.tradesperson_sig || insp.supervisor_sig) && (
            <SignaturesPanel insp={insp} onOpenPhoto={setLightbox} />
          )}

          {/* Supervisor sign-off form — used to live in a huge sticky footer
              that hid the review panel above on small screens. Now inline in
              the scrollable body so the whole flow (review → sections →
              sign-off → signature) is one continuous scroll. */}
          {!loading && insp && !isFinalised && isSubmitted && isSupervisor && !sendBackOpen && (
            <>
              <div className="panel-flat p-3 md:p-4" data-testid="signoff-details-panel">
                <div className="text-[10px] uppercase tracking-widest font-black text-muted mb-2 flex items-center gap-1.5">
                  <ClipboardCheck className="w-3.5 h-3.5" /> Sign-off details
                  {(smuValue !== "" && smuValue != null) || workOrderNumber ? (
                    <span className="ml-2 text-[9px] text-muted normal-case tracking-normal font-normal">
                      · synced with the WO# / SMU chips above
                    </span>
                  ) : null}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div className="field !mb-0">
                    <label className="text-[10px]">SMU (hrs)</label>
                    <input
                      className="input tabular-nums"
                      type="number"
                      min="0"
                      step="0.1"
                      inputMode="decimal"
                      placeholder="e.g. 5678"
                      value={signOffSmu}
                      onChange={(e) => setSignOffSmu(e.target.value)}
                      onBlur={commitSignOffSmu}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
                      data-testid="signoff-smu"
                    />
                  </div>
                  <div className="field !mb-0">
                    <label className="text-[10px]">Work Order #</label>
                    <input
                      className="input"
                      placeholder="e.g. WO-12345"
                      value={signOffWO}
                      onChange={(e) => setSignOffWO(e.target.value)}
                      onBlur={commitSignOffWO}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
                      data-testid="signoff-wo"
                    />
                  </div>
                </div>
              </div>
              <div ref={signaturePanelRef} className="panel-flat p-3 md:p-4" data-testid="signature-panel">
                <div className="text-[10px] uppercase tracking-widest font-black text-muted mb-2 flex items-center gap-1.5">
                  <PenTool className="w-3.5 h-3.5" /> Draw your signature to approve
                </div>
                <SignaturePad onChange={setSignature} label="Supervisor signature" height={140} />
              </div>
            </>
          )}

          {msg && (
            <div className={`pill ${msg.type === "ok" ? "pill-green" : "pill-red"} !text-xs !py-2 !px-3 justify-start`} data-testid="inspection-msg">
              {msg.type === "ok" ? <Check className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />} {msg.text}
            </div>
          )}
        </div>

        {/* Fixed footer action — kept compact so the scrolling body (with
            ReviewSummary at the top) is always visible on small screens. */}
        <div
          className="p-4 sticky bottom-0 z-10"
          style={{ borderTop: "1px solid var(--border)", background: "var(--surface)" }}
          data-testid="inspection-footer"
        >
          {isFinalised ? (
            <div className="pill pill-green !py-2.5 !px-4 justify-center">
              <CheckCircle2 className="w-4 h-4" /> Approved · locked
            </div>
          ) : isSubmitted && isSupervisor ? (
            /* Supervisor actions — one-tap Approve & Sign */
            !sendBackOpen ? (
              <div className="flex gap-2">
                <button
                  className="btn"
                  onClick={() => setSendBackOpen(true)}
                  disabled={busy}
                  data-testid="inspection-send-back-open"
                  style={{ background: "#5a0f0f", border: "1px solid #a51919", color: "#ff9b9b", minHeight: 56 }}
                >
                  <Undo2 className="w-5 h-5" /> Send Back
                </button>
                <button
                  className="btn btn-primary flex-1"
                  style={{ minHeight: 56, fontSize: 16, letterSpacing: "0.05em" }}
                  onClick={() => {
                    if (!signature) {
                      // Nothing drawn yet — scroll them straight to the sig
                      // pad so they don't have to hunt for it.
                      signaturePanelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                      return;
                    }
                    approve();
                  }}
                  disabled={busy}
                  data-testid="inspection-approve-submit"
                  title={!signature ? "Tap to jump to signature pad" : "Approve & sign"}
                >
                  {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
                  {signature ? "APPROVE & SIGN" : "SIGN TO APPROVE"}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-[11px] uppercase tracking-widest font-black text-red-300 flex items-center gap-1.5">
                  <Undo2 className="w-3.5 h-3.5" /> Reason for sending back
                </div>
                <textarea
                  className="input"
                  rows={2}
                  value={sendBackReason}
                  onChange={(e) => setSendBackReason(e.target.value)}
                  placeholder="e.g. Pin temps 15–22 not recorded — re-check after run."
                  data-testid="inspection-send-back-reason"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button className="btn btn-ghost flex-1" onClick={() => { setSendBackOpen(false); setSendBackReason(""); }}>Cancel</button>
                  <button
                    className="btn flex-1"
                    style={{ background: "#a51919", color: "#fff", minHeight: 56 }}
                    onClick={sendBack}
                    disabled={busy || !sendBackReason.trim()}
                    data-testid="inspection-send-back-submit"
                  >
                    {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} Send Back
                  </button>
                </div>
              </div>
            )
          ) : (
            /* Tradesperson action */
            <button
              className="btn btn-primary w-full"
              style={{ minHeight: 60, fontSize: 17, letterSpacing: "0.05em" }}
              onClick={submitForSignoff}
              disabled={busy || stats.open === stats.total}
              data-testid="inspection-submit-signoff"
            >
              {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <Send className="w-6 h-6" />}
              SUBMIT FOR SIGN-OFF
            </button>
          )}
        </div>

        <EmailModal
          open={emailOpen}
          onClose={() => setEmailOpen(false)}
          item={insp || {}}
          kind="Inspection"
        />

        {showDelete && (
          <ConfirmDeleteInspection
            asset={insp?.asset_id || "this inspection"}
            template={insp?.template_name}
            busy={deleting}
            onCancel={() => setShowDelete(false)}
            onConfirm={deleteInspection}
          />
        )}

        {/* Fullscreen photo lightbox — accepts either a URL string (plain
            photos from signatures / etc.) or a markup object
            { url, svg, annotated } so the technician's SVG overlay renders
            at full resolution. */}
        {lightbox && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.92)" }}
            onClick={() => setLightbox(null)}
            data-testid="photo-lightbox"
          >
            {typeof lightbox === "string" ? (
              <img
                src={lightbox}
                alt="Attachment"
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <div
                className="relative max-w-full max-h-full"
                style={{ width: "min(95vw, 1400px)", height: "min(90vh, 900px)" }}
                onClick={(e) => e.stopPropagation()}
              >
                <MarkupImage
                  url={lightbox.url}
                  svg={lightbox.svg}
                  alt={lightbox.annotated ? "Marked-up photo" : "Photo"}
                  className="w-full h-full rounded-lg shadow-2xl overflow-hidden"
                />
                {lightbox.annotated && (
                  <span
                    className="absolute top-3 left-3 px-2 py-1 rounded text-[10px] tracking-widest font-black shadow-lg z-10"
                    style={{ background: "var(--primary)", color: "#000" }}
                    data-testid="photo-lightbox-marked"
                  >
                    MARKED BY TECHNICIAN
                  </span>
                )}
              </div>
            )}
            <button
              className="btn btn-sm btn-ghost absolute top-4 right-4"
              onClick={() => setLightbox(null)}
              data-testid="photo-lightbox-close"
            >
              <X className="w-4 h-4" /> Close
            </button>
          </div>
        )}
      </aside>
    </div>
  );
}

/* ---------------- Section ---------------- */
function SignaturesPanel({ insp, onOpenPhoto }) {
  const trade = signatureSrc(insp.tradesperson_sig);
  const sup = signatureSrc(insp.supervisor_sig);
  const cell = (role, sigSrc, name, when) => (
    <div
      className="panel-flat p-4"
      style={{ background: "#fafafa", color: "#111" }}
      data-testid={`sig-${role.toLowerCase()}`}
    >
      <div className="text-[10px] uppercase tracking-widest font-black text-neutral-500">
        {role}
      </div>
      <div
        className="mt-2 rounded-lg overflow-hidden flex items-center justify-center cursor-zoom-in"
        style={{ background: "#fff", border: "1px solid #e5e5e5", minHeight: 120 }}
        onClick={sigSrc ? () => onOpenPhoto?.(sigSrc) : undefined}
      >
        {sigSrc ? (
          <img
            src={sigSrc}
            alt={`${role} signature`}
            className="w-full h-auto max-h-40 object-contain"
            data-testid={`sig-${role.toLowerCase()}-img`}
          />
        ) : (
          <div className="py-8 text-neutral-400 text-sm italic">— unsigned —</div>
        )}
      </div>
      <div className="mt-2">
        <div className="font-display font-black text-sm text-neutral-900" data-testid={`sig-${role.toLowerCase()}-name`}>
          {name || "—"}
        </div>
        <div className="text-[11px] text-neutral-500 font-mono-tight mt-0.5">
          {fmtSigDate(when)}
        </div>
      </div>
    </div>
  );
  return (
    <section data-testid="signatures-panel">
      <div className="text-[11px] uppercase tracking-[0.3em] font-black mb-3" style={{ color: "var(--primary)" }}>
        Signatures
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {cell("Tradesperson", trade, insp.started_by_name || insp.started_by, insp.started_at)}
        {cell("Supervisor", sup, insp.supervisor_name, insp.approved_at || insp.updated_at)}
      </div>
    </section>
  );
}

function fmtSigDate(v) {
  if (!v) return "";
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
}

/* ---------------- Sign-off review panel ----------------
 * Compact "at-a-glance" summary of every task in the inspection, grouped
 * by section. Supervisors can scan this before scrolling down to sign.
 * Each section can be expanded to reveal every item's exact status.
 */
function ReviewSummary({ insp, allItems, itemsState }) {
  const [open, setOpen] = useState(true);
  const sections = insp?.sections || [];
  // Auto-expand every section on first open so the supervisor immediately
  // sees every check + wear reading. They can collapse any section they
  // don't need to inspect further.
  const [expandedSecs, setExpandedSecs] = useState(() => new Set(sections.map((s) => s.id)));
  // Precompute per-section counts
  const summarize = (items) => {
    const c = { ok:0, issue:0, na:0, ppf:0, open:0, wearNew:0, w25:0, w50:0, w75:0, w100:0 };
    items.forEach((it) => {
      const st = itemsState[it.id]?.status;
      const s = String(st ?? "").toLowerCase();
      if (it.kind === "wear_pct") {
        if (s === "new" || s === "0") c.wearNew++;
        else if (s === "25") c.w25++;
        else if (s === "50") c.w50++;
        else if (s === "75") c.w75++;
        else if (s === "100") c.w100++;
        else c.open++;
        return;
      }
      if (s === "ok") c.ok++;
      else if (s === "issue") { c.issue++; if (itemsState[it.id]?.action_priority) c.ppf++; }
      else if (s === "na") c.na++;
      else c.open++;
    });
    return c;
  };
  const totals = summarize(allItems);
  const chip = (label, n, color) => n > 0 ? (
    <span
      className="pill !text-[10px] !py-0.5 !px-2 tabular-nums"
      style={{ background: `${color}22`, border: `1px solid ${color}55`, color }}
    >
      {label} · {n}
    </span>
  ) : null;
  const issueItems = allItems.filter((it) => {
    const s = String(itemsState[it.id]?.status ?? "").toLowerCase();
    return s === "issue";
  });

  // Status pill for one item — colour + label
  const itemPill = (it) => {
    const st = itemsState[it.id] || {};
    const s = String(st.status ?? "").toLowerCase();
    if (it.kind === "wear_pct") {
      if (s === "new" || s === "0") return { label: "NEW", color: "#4ade80" };
      if (s === "25") return { label: "25%", color: "#4ade80" };
      if (s === "50") return { label: "50%", color: "#facc15" };
      if (s === "75") return { label: "75%", color: "#ffb347" };
      if (s === "100") return { label: "C/O", color: "#ff9b9b" };
      return { label: "OPEN", color: "#f58220" };
    }
    if (s === "ok") return { label: "OK", color: "#4ade80" };
    if (s === "issue") return { label: "ISSUE", color: "#ff9b9b" };
    if (s === "na") return { label: "N/A", color: "#a1a1aa" };
    return { label: "OPEN", color: "#f58220" };
  };
  const toggleSec = (id) => {
    setExpandedSecs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const expandAll = () => setExpandedSecs(new Set(sections.map((s) => s.id)));
  const collapseAll = () => setExpandedSecs(new Set());

  return (
    <div
      className="panel-accent"
      style={{ boxShadow: "0 0 0 1px var(--primary-glow) inset, 0 20px 48px -18px rgba(245, 130, 32, 0.35)" }}
      data-testid="review-summary"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 p-4 md:p-5 text-left hover:bg-white/5 transition rounded-[inherit]"
        data-testid="review-summary-toggle"
      >
        <div
          className="w-11 h-11 shrink-0 rounded-xl flex items-center justify-center"
          style={{ background: "rgba(245,130,32,0.18)", border: "1px solid rgba(245,130,32,0.45)", color: "var(--primary)" }}
        >
          <ClipboardCheck className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.35em] font-black" style={{ color: "var(--primary)" }}>
            Sign-off review · scan before signing
          </div>
          <div className="font-display font-black text-lg md:text-xl leading-tight mt-0.5 tracking-tight">
            EVERY ITEM YOUR CREW COMPLETED
          </div>
        </div>
        <div className="hidden md:flex flex-wrap gap-1.5 justify-end">
          {chip("OK", totals.ok, "#4ade80")}
          {chip("ISSUE", totals.issue, "#ff9b9b")}
          {chip("N/A", totals.na, "#a1a1aa")}
          {chip("PPF", totals.ppf, "#ffb347")}
          {chip("NEW", totals.wearNew, "#4ade80")}
          {chip("25%", totals.w25, "#4ade80")}
          {chip("50%", totals.w50, "#facc15")}
          {chip("75%", totals.w75, "#ffb347")}
          {chip("C/O", totals.w100, "#ff9b9b")}
          {chip("OPEN", totals.open, "#f58220")}
        </div>
        {open ? <ChevronUp className="w-5 h-5 shrink-0" style={{ color: "var(--primary)" }} /> : <ChevronDown className="w-5 h-5 shrink-0" style={{ color: "var(--primary)" }} />}
      </button>

      {open && (
        <div className="border-t border-white/10 p-3 md:p-4 space-y-3" data-testid="review-summary-body">
          {/* Bulk expand/collapse controls */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={expandAll}
              className="text-[10px] uppercase tracking-widest font-black text-muted hover:text-white transition"
              data-testid="review-expand-all"
            >
              Expand all
            </button>
            <span className="text-muted">·</span>
            <button
              type="button"
              onClick={collapseAll}
              className="text-[10px] uppercase tracking-widest font-black text-muted hover:text-white transition"
              data-testid="review-collapse-all"
            >
              Collapse all
            </button>
            <div className="ml-auto text-[10px] uppercase tracking-widest text-muted tabular-nums">
              {allItems.length - totals.open} of {allItems.length} answered
            </div>
          </div>

          {issueItems.length > 0 && (
            <div className="rounded-lg p-3" style={{ background: "rgba(255,155,155,0.08)", border: "1px solid rgba(255,155,155,0.3)" }}>
              <div className="text-[10px] uppercase tracking-widest font-black mb-2" style={{ color: "#ff9b9b" }}>
                <AlertTriangle className="w-3 h-3 inline -mt-0.5 mr-1" /> Items flagged as issue · {issueItems.length}
              </div>
              <ul className="space-y-1">
                {issueItems.slice(0, 8).map((it) => {
                  const st = itemsState[it.id] || {};
                  return (
                    <li key={it.id} className="text-xs flex items-start gap-2" data-testid={`review-issue-${it.id}`}>
                      <span className="w-1 h-1 rounded-full mt-1.5 shrink-0" style={{ background: "#ff9b9b" }} />
                      <span className="flex-1">
                        <span className="font-black">{it.label}</span>
                        {st.action_priority && <span className="text-muted"> · priority {st.action_priority}</span>}
                        {st.action_required && <span className="text-muted"> — {st.action_required}</span>}
                      </span>
                    </li>
                  );
                })}
                {issueItems.length > 8 && (
                  <li className="text-xs text-muted italic">+ {issueItems.length - 8} more — scroll below for details</li>
                )}
              </ul>
            </div>
          )}
          {sections.map((sec) => {
            const items = sec.items || [];
            const c = summarize(items);
            const total = items.length;
            const answered = total - c.open;
            const isOpen = expandedSecs.has(sec.id);
            return (
              <div key={sec.id} className="rounded-lg" style={{ border: "1px solid rgba(255,255,255,0.07)" }} data-testid={`review-section-${sec.id}`}>
                <button
                  type="button"
                  onClick={() => toggleSec(sec.id)}
                  className="w-full flex flex-wrap items-center gap-2 py-2 px-3 text-left hover:bg-white/5 transition"
                  data-testid={`review-section-toggle-${sec.id}`}
                >
                  {isOpen
                    ? <ChevronUp className="w-3 h-3 shrink-0 text-muted" />
                    : <ChevronDown className="w-3 h-3 shrink-0 text-muted" />}
                  <div className="flex-1 min-w-[180px]">
                    <div className="text-[10px] uppercase tracking-widest font-black text-muted">{sec.name}</div>
                    <div className="text-xs mt-0.5 tabular-nums">
                      <span className="font-black" style={{ color: answered === total ? "#4ade80" : "var(--primary)" }}>
                        {answered}/{total}
                      </span>
                      <span className="text-muted"> answered</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 justify-end">
                    {chip("OK", c.ok, "#4ade80")}
                    {chip("ISSUE", c.issue, "#ff9b9b")}
                    {chip("N/A", c.na, "#a1a1aa")}
                    {chip("PPF", c.ppf, "#ffb347")}
                    {chip("NEW", c.wearNew, "#4ade80")}
                    {chip("25%", c.w25, "#4ade80")}
                    {chip("50%", c.w50, "#facc15")}
                    {chip("75%", c.w75, "#ffb347")}
                    {chip("C/O", c.w100, "#ff9b9b")}
                    {chip("OPEN", c.open, "#f58220")}
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t border-white/10 divide-y divide-white/5" data-testid={`review-section-items-${sec.id}`}>
                    {items.map((it) => {
                      const pill = itemPill(it);
                      const st = itemsState[it.id] || {};
                      return (
                        <div
                          key={it.id}
                          className="flex items-start gap-3 px-3 py-2 hover:bg-white/[0.03] transition"
                          data-testid={`review-item-${it.id}`}
                        >
                          <span
                            className="pill !text-[9px] !py-0.5 !px-1.5 shrink-0 mt-0.5 tabular-nums"
                            style={{
                              background: `${pill.color}22`,
                              border: `1px solid ${pill.color}55`,
                              color: pill.color,
                              minWidth: "44px",
                              justifyContent: "center",
                            }}
                          >
                            {pill.label}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-black leading-tight">{it.label}</div>
                            {st.action_required && (
                              <div className="text-[10px] text-muted mt-0.5 line-clamp-1">
                                {st.action_priority && <span className="uppercase tracking-widest mr-1">P{st.action_priority} ·</span>}
                                {st.action_required}
                              </div>
                            )}
                            {st.clicked_by_name && (
                              <div className="text-[10px] text-muted mt-0.5">
                                {st.clicked_by_name}
                                {st.clicked_at && ` · ${new Date(st.clicked_at).toLocaleString([], { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" })}`}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------------- Section ---------------- */
function SectionBlock({ section, itemsState, onStatus, onValue, onExtras, onOpenPhoto, savingItems, disabled, onApplyToAllWear, inspection }) {
  const [markupOpen, setMarkupOpen] = useState(false);
  const allItems = section.items || [];
  // Wear items get the compact bucket-wear grid; everything else stays as
  // the standard TaskCard row so measurement / check / pin-matrix items
  // keep their existing UX.
  const wearItems = allItems.filter((it) => it.kind === "wear_pct");
  const otherItems = allItems.filter((it) => it.kind !== "wear_pct");
  return (
    <section data-testid={`section-${section.id}`}>
      <div className="text-[11px] uppercase tracking-[0.3em] font-black mb-3" style={{ color: "var(--primary)" }}>
        {section.name}
      </div>

      {section.intro && (
        <div className="panel-flat p-4 mb-3 text-sm whitespace-pre-line" style={{ color: "#d4d4d8" }}>
          {section.intro}
        </div>
      )}

      {section.ref_image && (
        <div className="panel p-3 mb-3">
          <img
            src={section.ref_image}
            alt="Reference"
            className="w-full rounded-lg object-contain max-h-[320px]"
            style={{ background: "#0a0a0a" }}
            data-testid={`section-image-${section.id}`}
          />
          <button
            className="btn btn-sm btn-ghost mt-2"
            onClick={() => setMarkupOpen(true)}
            data-testid={`section-markup-${section.id}`}
          >
            <ImageIcon className="w-4 h-4" /> Tap to Mark Up
          </button>
          {markupOpen && (
            <div className="text-[11px] text-muted mt-2 font-mono-tight">
              Mark-up capture is available on the mobile app — the tradesperson&apos;s markups will appear here after they submit.
            </div>
          )}
        </div>
      )}

      <div className="space-y-3">
        {wearItems.length > 0 && (
          <BucketWearGrid
            items={wearItems}
            itemsState={itemsState}
            savingItems={savingItems}
            disabled={disabled}
            onStatus={onStatus}
            onApplyToAll={onApplyToAllWear}
            testid={`section-${section.id}-wear-grid`}
          />
        )}
        {otherItems.map((item) => (
          <TaskCard
            key={item.id}
            item={item}
            state={itemsState[item.id] || {}}
            onStatus={onStatus}
            onValue={onValue}
            onExtras={onExtras}
            onOpenPhoto={onOpenPhoto}
            saving={savingItems?.has(item.id)}
            disabled={disabled}
            inspection={inspection}
          />
        ))}
      </div>
    </section>
  );
}

/* ---------------- Task card ---------------- */
function TaskCard({ item, state, onStatus, onValue, onExtras, onOpenPhoto, saving, disabled, inspection }) {
  const kind = item.kind || "check";
  const photos = mergePhotosAndMarkups(state.photos, state.markups);
  const isIssue = String(state.status || "").toLowerCase() === "issue";
  const hasPpf = !!(state.extras?.ppf_id || state.ppf_id);
  return (
    <div className="panel-heavy p-4" data-testid={`task-${item.id}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-display font-black text-base tracking-tight leading-snug">
            {item.label}
          </div>
          {item.help_text && (
            <div className="text-[11px] text-muted mt-1 whitespace-pre-line">{item.help_text}</div>
          )}
        </div>
        <AuditBadge state={state} saving={saving} item={item} />
      </div>

      <div className="mt-3">
        {(kind === "check" || kind === "condition") && (
          <CheckControls
            state={state}
            onStatus={(s) => onStatus(item, s)}
            disabled={disabled}
            testid={`task-controls-${item.id}`}
          />
        )}
        {kind === "wear_pct" && (
          <WearPctControl
            state={state}
            onStatus={(s) => onStatus(item, s)}
            disabled={disabled}
            testid={`task-wear-${item.id}`}
          />
        )}
        {kind === "measurement" && (
          <MeasurementInput
            state={state}
            unit={item.unit}
            onValue={(v) => onValue(item, v)}
            disabled={disabled}
            testid={`task-measurement-${item.id}`}
          />
        )}
        {kind === "pin_matrix" && (
          <PinMatrix
            item={item}
            state={state}
            onExtras={(extras) => onExtras(item, extras)}
            disabled={disabled}
          />
        )}
        {kind === "selector" && (
          <SelectorControl
            item={item}
            state={state}
            onValue={(v) => onValue(item, v)}
            disabled={disabled}
            testid={`task-selector-${item.id}`}
          />
        )}
      </div>

      {/* Photos attached to this task — larger, uncropped thumbnails so
          circled/annotated damage is visible at a glance. Annotated
          (markup) photos are shown first with a "MARKED" chip and the
          technician's overlay SVG rendered on top of the base image. */}
      {photos.length > 0 && (
        <div className="mt-3" data-testid={`task-photos-${item.id}`}>
          <div className="text-[10px] uppercase tracking-widest font-black text-muted mb-1.5 flex items-center gap-1">
            <ImageIcon className="w-3 h-3" /> Photos ({photos.length})
            {photos.some((p) => p.annotated) && (
              <span
                className="ml-1.5 px-1.5 py-0.5 rounded-sm text-[9px] tracking-wider font-black"
                style={{ background: "var(--primary)", color: "#000" }}
                data-testid={`task-has-markup-${item.id}`}
              >
                MARKED UP
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {photos.map((p, i) => (
              <button
                key={i}
                onClick={() => onOpenPhoto?.(p)}
                className="relative rounded-lg overflow-hidden group shrink-0"
                style={{ width: 112, height: 112, background: "#0a0a0a", border: "1px solid var(--border)" }}
                data-testid={`task-photo-${item.id}-${i}`}
                title={p.annotated ? "Marked-up photo — tap to enlarge" : "Tap to enlarge"}
              >
                <MarkupImage
                  url={p.url}
                  svg={p.svg}
                  alt={p.annotated ? `Marked-up photo ${i + 1}` : `Photo ${i + 1}`}
                  className="w-full h-full"
                />
                {p.annotated && (
                  <span
                    className="absolute top-1 left-1 px-1.5 py-0.5 rounded-sm text-[9px] tracking-wider font-black shadow z-10"
                    style={{ background: "var(--primary)", color: "#000" }}
                    data-testid={`task-photo-marked-${item.id}-${i}`}
                  >
                    MARKED
                  </span>
                )}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                     style={{ background: "rgba(0,0,0,0.5)" }}>
                  <ZoomIn className="w-5 h-5 text-white" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Comment left by the tradesperson */}
      {state.comment && (
        <div className="mt-3 panel-flat p-2.5 text-xs text-white/90 whitespace-pre-wrap" data-testid={`task-comment-${item.id}`}>
          <span className="text-[10px] uppercase tracking-widest font-black text-muted mr-2">Note</span>
          {state.comment}
        </div>
      )}

      {/* ISSUE → PPF escalation panel. Shows inline whenever the item is
          currently flagged as an ISSUE so the tradesperson can raise a
          Planning Request without leaving the checklist. Josh's spec:
          "put PPF in if there is a issue which auto goes into PPF section
          and then i finish it off after inspection complete." */}
      {isIssue && !disabled && (
        <RaisePpfPanel
          item={item}
          state={state}
          hasPpf={hasPpf}
          inspection={inspection}
          onSaveExtras={(extras) => onExtras(item, extras)}
        />
      )}
    </div>
  );
}

/* ---------------- Controls per kind ---------------- */
function CheckControls({ state, onStatus, disabled, testid }) {
  const s = state.status || "";
  const btn = (id, label, Icon, bg, fg, border) => (
    <button
      key={id}
      type="button"
      disabled={disabled}
      onClick={() => onStatus(id)}
      className="flex-1 py-3 rounded-xl flex items-center justify-center gap-2 font-display font-black uppercase tracking-widest text-sm transition-all duration-150 disabled:opacity-40 active:scale-95 select-none"
      style={{
        background: s === id ? bg : "var(--surface-2)",
        color: s === id ? fg : "#e5e7eb",
        border: `1px solid ${s === id ? border : "var(--border)"}`,
        boxShadow: s === id ? `inset 0 0 0 1px ${border}` : undefined,
      }}
      data-testid={`${testid}-${id}`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
  return (
    <div className="grid grid-cols-3 gap-2" data-testid={testid}>
      {btn("ok",    "OK",    CheckCircle2, "#0a2a12", "#4ade80", "#1e6b32")}
      {btn("issue", "ISSUE", XCircle,      "#5a0f0f", "#ff9b9b", "#a51919")}
      {btn("na",    "N/A",   MinusCircle,  "#141414", "#a1a1aa", "#3a3a3a")}
    </div>
  );
}

function MeasurementInput({ state, unit, onValue, disabled, testid }) {
  const [v, setV] = useState(state.value ?? "");
  const [saving, setSaving] = useState(false);
  useEffect(() => setV(state.value ?? ""), [state.value]);

  const commit = async () => {
    if (v === "" || String(v) === String(state.value ?? "")) return;
    setSaving(true);
    try { await onValue(v); }
    finally { setSaving(false); }
  };

  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <Ruler className="w-4 h-4 text-muted" />
      <input
        type="number"
        inputMode="decimal"
        step="any"
        className="input flex-1 font-mono-tight text-lg"
        style={{ borderColor: "var(--primary)" }}
        value={v}
        onChange={(e) => setV(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
        placeholder="—"
        disabled={disabled}
      />
      {unit && <span className="text-sm font-mono-tight text-muted min-w-[32px]">{unit}</span>}
      {saving && <Loader2 className="w-4 h-4 animate-spin text-muted" />}
      {!saving && String(v) !== "" && String(v) === String(state.value ?? "") && (
        <span className="pill pill-green !text-[9px] !py-0.5 !px-1.5" title="Saved"><Check className="w-3 h-3" /></span>
      )}
    </div>
  );
}

function WearPctControl({ state, onStatus, disabled, testid }) {
  // Mobile Pit Link app stores wear as a status token STRING: "new" for
  // brand-new components (0 % worn), then "25" / "50" / "75" / "100" as they
  // wear down. Everything (mobile ↔ portal) MUST send/read strings so the
  // two clients speak the same language.
  const raw = state?.status;
  const isNew = String(raw || "").toLowerCase() === "new" || raw === 0 || raw === "0";
  const currentPct = isNew ? null : (raw != null && raw !== "" ? Number(raw) : null);
  const options = [
    { key: "new", pct: null, label: "NEW",      bg: "#0a2a12", border: "#4ade80", color: "#4ade80" },
    { key: "25",  pct: 25,   label: "25% WORN", bg: "#0a2a12", border: "#1e6b32", color: "#4ade80" },
    { key: "50",  pct: 50,   label: "50% WORN", bg: "#3a2f0d", border: "#c9a227", color: "#facc15" },
    { key: "75",  pct: 75,   label: "75% WORN", bg: "#4a2a06", border: "#f58220", color: "#ffb347" },
  ];
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => {
        const on = o.key === "new" ? isNew : currentPct === o.pct;
        return (
          <button
            key={o.key}
            // Always send the status as a STRING so it round-trips 1:1 with the
            // mobile app ("new" | "25" | "50" | "75").
            onClick={() => onStatus(o.key)}
            disabled={disabled}
            data-testid={`${testid}-${o.key}`}
            className="px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest transition-colors tabular-nums"
            style={on
              ? { background: o.bg, border: `1px solid ${o.border}`, color: o.color }
              : { background: "var(--surface-2)", border: "1px solid var(--border)", color: "#e5e7eb" }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function SelectorControl({ item, state, onValue, disabled, testid }) {
  const options = item.options || item.choices || [];
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((opt) => {
        const val = typeof opt === "string" ? opt : opt.value;
        const label = typeof opt === "string" ? opt : (opt.label || opt.value);
        const on = String(state.value_str || state.value || "") === String(val);
        return (
          <button
            key={val}
            onClick={() => onValue(val)}
            disabled={disabled}
            className="px-3.5 py-2 rounded-full text-xs font-black uppercase tracking-widest transition-colors"
            style={on
              ? { background: "var(--primary)", color: "#0a0a0a" }
              : { background: "var(--surface-2)", border: "1px solid var(--border)", color: "#e5e7eb" }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

/* ---------------- Pin matrix (44 pins) ---------------- */
function PinMatrix({ item, state, onExtras, disabled }) {
  const count = item.pin_count || 44;
  const rules = item.rules?.auto_flag || { ok_max_above_avg: 5, relube_max_above_avg: 10, absolute_ppf: 35 };
  // The mobile app writes pin data as extras.values (an ARRAY of 44 numbers)
  // plus extras.average / extras.ok_count / relube_count / fail_count / ppf_count.
  // Older/portal writes used extras.pins (an object). Read BOTH shapes so we
  // never lose data written by the app or an older client.
  const readPins = (st) => {
    if (!st) return Array.from({ length: count }, () => "");
    const ext = st.extras || {};
    if (Array.isArray(ext.values)) {
      return Array.from({ length: count }, (_, i) => {
        const v = ext.values[i];
        return v == null || v === "" ? "" : String(v);
      });
    }
    const src = st.pins || ext.pins;
    if (src && typeof src === "object") {
      return Array.from({ length: count }, (_, i) => {
        const v = src[i + 1] ?? src[String(i + 1)] ?? src[i];
        return v == null || v === "" ? "" : String(v);
      });
    }
    return Array.from({ length: count }, () => "");
  };
  const readTrack = (st) => (st && (st.extras?.track_id ?? st.track_id ?? "")) || "";

  const [pins, setPins] = useState(() => readPins(state));
  const [trackId, setTrackId] = useState(() => readTrack(state));
  const [bulk, setBulk] = useState("");
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [rangeVal, setRangeVal] = useState("");

  useEffect(() => {
    setPins(readPins(state));
    setTrackId(readTrack(state));
  }, [state, count]);

  const numeric = pins.map((v) => Number(v)).filter((n) => !isNaN(n) && n !== 0);
  const avg = numeric.length ? numeric.reduce((a, b) => a + b, 0) / numeric.length : 0;

  const flag = (val) => {
    if (val === "" || isNaN(Number(val))) return null;
    const n = Number(val);
    if (n >= rules.absolute_ppf) return "ppf";
    if (!numeric.length) return null;
    const diff = n - avg;
    if (diff >= rules.relube_max_above_avg) return "ppf";
    if (diff >= rules.ok_max_above_avg) return "relube";
    return "ok";
  };

  const counts = { ok: 0, relube: 0, fail: 0 };
  pins.forEach((v) => {
    const f = flag(v);
    if (f === "ok") counts.ok++;
    if (f === "relube") counts.relube++;
    if (f === "ppf") counts.fail++;
  });

  const commit = (nextPins, nextTrackId = trackId) => {
    // Write in the EXACT shape the mobile app uses so app / portal stay in sync.
    const values = nextPins.map((v) => (v === "" || v == null ? null : Number(v)));
    const nums = values.filter((n) => typeof n === "number" && !isNaN(n) && n !== 0);
    const nextAvg = nums.length ? Number((nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(1)) : null;
    // Recompute counts against the *new* values (state's counts are stale here).
    const nextCounts = { ok: 0, relube: 0, fail: 0, ppf: 0 };
    values.forEach((n) => {
      if (n == null || isNaN(n)) return;
      if (n >= rules.absolute_ppf) { nextCounts.fail++; nextCounts.ppf++; return; }
      if (nextAvg != null) {
        const d = n - nextAvg;
        if (d >= rules.relube_max_above_avg) { nextCounts.fail++; nextCounts.ppf++; return; }
        if (d >= rules.ok_max_above_avg) { nextCounts.relube++; return; }
      }
      nextCounts.ok++;
    });
    onExtras({
      value: nextAvg,
      extras: {
        values,
        average: nextAvg,
        ok_count: nextCounts.ok,
        relube_count: nextCounts.relube,
        fail_count: nextCounts.fail,
        ppf_count: nextCounts.ppf,
        track_id: nextTrackId,
      },
    });
  };

  const bulkFill = () => {
    if (bulk === "") return;
    const next = pins.map(() => bulk);
    setPins(next);
    commit(next);
  };
  const rangeFill = () => {
    const s = parseInt(rangeStart, 10);
    const e = parseInt(rangeEnd, 10);
    if (isNaN(s) || isNaN(e) || rangeVal === "" || s < 1 || e > count || s > e) return;
    const next = pins.slice();
    for (let i = s - 1; i <= e - 1; i++) next[i] = rangeVal;
    setPins(next);
    commit(next);
  };
  const setOne = (idx, v) => {
    const next = pins.slice();
    next[idx] = v;
    setPins(next);
  };
  const blur = () => commit(pins);

  return (
    <div className="space-y-3" data-testid={`pin-matrix-${item.id}`}>
      {/* Stats summary */}
      <div className="grid grid-cols-4 gap-2">
        <MiniStat label="AVG" value={numeric.length ? avg.toFixed(1) : "—"} suffix="°C" color="var(--primary)" />
        <MiniStat label="OK" value={counts.ok} color="#4ade80" />
        <MiniStat label="RELUBE" value={counts.relube} color="#ffb347" />
        <MiniStat label="FAIL" value={counts.fail} color="#ff9b9b" />
      </div>

      {/* Bulk fill + Range fill */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div className="panel p-2 flex items-center gap-2">
          <div className="text-[10px] uppercase tracking-widest font-black text-muted whitespace-nowrap">Bulk fill</div>
          <input
            type="number"
            className="input !py-1.5 flex-1 font-mono-tight"
            value={bulk}
            onChange={(e) => setBulk(e.target.value)}
            placeholder="°C"
            disabled={disabled}
            data-testid={`pin-matrix-bulk-${item.id}`}
          />
          <button className="btn btn-sm btn-primary" onClick={bulkFill} disabled={disabled || bulk === ""}>Fill</button>
        </div>
        <div className="panel p-2 flex items-center gap-1.5">
          <div className="text-[10px] uppercase tracking-widest font-black text-muted whitespace-nowrap">Range</div>
          <input type="number" className="input !py-1.5 !w-14 font-mono-tight" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} placeholder="1" min={1} max={count} disabled={disabled} data-testid={`pin-matrix-range-start-${item.id}`} />
          <span className="text-muted">→</span>
          <input type="number" className="input !py-1.5 !w-14 font-mono-tight" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} placeholder={String(count)} min={1} max={count} disabled={disabled} data-testid={`pin-matrix-range-end-${item.id}`} />
          <input type="number" className="input !py-1.5 flex-1 font-mono-tight" value={rangeVal} onChange={(e) => setRangeVal(e.target.value)} placeholder="°C" disabled={disabled} data-testid={`pin-matrix-range-val-${item.id}`} />
          <button className="btn btn-sm btn-primary" onClick={rangeFill} disabled={disabled}>Fill</button>
        </div>
      </div>

      {item.track_id_field && (
        <div className="flex items-center gap-2">
          <div className="text-[10px] uppercase tracking-widest font-black text-muted whitespace-nowrap">Track ID</div>
          <input
            className="input !py-1.5 max-w-[240px] font-mono-tight"
            value={trackId}
            onChange={(e) => setTrackId(e.target.value)}
            onBlur={() => commit(pins, trackId)}
            placeholder="e.g. TL-04-A"
            disabled={disabled}
            data-testid={`pin-matrix-track-id-${item.id}`}
          />
        </div>
      )}

      {/* 44-pin grid */}
      <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-11 gap-1.5">
        {pins.map((v, i) => {
          const f = flag(v);
          const tone =
            f === "ppf" ? { bg: "#5a0f0f", border: "#a51919", color: "#ff9b9b" } :
            f === "relube" ? { bg: "#4a2a06", border: "#f58220", color: "#ffb347" } :
            f === "ok" ? { bg: "#0a2a12", border: "#1e6b32", color: "#4ade80" } :
            { bg: "var(--surface-2)", border: "var(--border)", color: "#e5e7eb" };
          return (
            <div key={i} className="panel-flat flex flex-col items-stretch" style={{ borderColor: tone.border, background: tone.bg }} data-testid={`pin-${item.id}-${i + 1}`}>
              <div className="text-[9px] font-black text-center px-1 pt-0.5 font-mono-tight" style={{ color: tone.color }}>{i + 1}</div>
              <input
                type="number"
                inputMode="numeric"
                step="any"
                className="w-full bg-transparent outline-none text-center font-mono-tight text-sm py-1"
                style={{ color: tone.color }}
                value={v}
                onChange={(e) => setOne(i, e.target.value)}
                onBlur={() => blur()}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
                disabled={disabled}
              />
            </div>
          );
        })}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-muted flex items-center gap-2">
        <Thermometer className="w-3 h-3" /> Auto-flagged against avg — OK ≤{rules.ok_max_above_avg}°C · RELUBE ≤{rules.relube_max_above_avg}°C · PPF ≥{rules.absolute_ppf}°C absolute
      </div>
    </div>
  );
}

function MiniStat({ label, value, suffix, color }) {
  return (
    <div className="panel-flat p-2 text-center">
      <div className="text-[9px] uppercase tracking-widest font-black text-muted">{label}</div>
      <div className="font-display font-black text-lg tabular-nums leading-none mt-1" style={{ color }}>
        {value}{suffix && <span className="text-[10px] text-muted"> {suffix}</span>}
      </div>
    </div>
  );
}

function Stat({ label, value, color, testid }) {
  return (
    <div className="text-center" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-black text-muted">{label}</div>
      <div className="font-display font-black text-2xl md:text-3xl tabular-nums leading-none mt-1" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

/* ---------------- Audit badge ---------------- */
function AuditBadge({ state, saving, item }) {
  const who = state.clicked_by;
  const when = state.clicked_at;
  const status = state.status;
  const hasValue =
    (state?.value !== null && state?.value !== undefined && state?.value !== "") ||
    state?.value_str ||
    state?.extras?.average != null ||
    (item?.kind === "wear_pct" && state?.status != null && state?.status !== "");
  if (!who && !when && !saving && !hasValue) return null;

  // wear_pct kinds store the tooth / lip / shroud wear percentage IN the
  // status field itself (e.g. status = "25" or 25 meaning 25% worn, or "new"
  // for a brand-new component). The API returns it as a numeric STRING —
  // coerce before doing maths.
  const kindHint = item?.kind || "check";
  const wearIsNew = kindHint === "wear_pct" && String(status || "").toLowerCase() === "new";
  const wearPctVal = (() => {
    if (kindHint !== "wear_pct") return null;
    if (wearIsNew) return 0;
    if (status == null || status === "") return null;
    const n = Number(status);
    return isNaN(n) ? null : n;
  })();
  const isWearPct = wearPctVal != null || wearIsNew;
  const wearTone = (n) =>
    n >= 100 ? { bg: "#5a0f0f", border: "#a51919", color: "#ff9b9b" } :
    n >= 75  ? { bg: "#4a2a06", border: "#f58220", color: "#ffb347" } :
    n >= 50  ? { bg: "#3a2f0d", border: "#c9a227", color: "#facc15" } :
               { bg: "#0a2a12", border: "#1e6b32", color: "#4ade80" };

  const s = String(status || "").toLowerCase();
  const tone = isWearPct
    ? { ...wearTone(wearPctVal), label: wearIsNew ? "NEW" : `${wearPctVal}%` }
    : s === "ok" ? { bg: "#0a2a12", border: "#1e6b32", color: "#4ade80", label: "OK" }
    : s === "issue" ? { bg: "#5a0f0f", border: "#a51919", color: "#ff9b9b", label: "ISSUE" }
    : s === "na" ? { bg: "#141414", border: "#3a3a3a", color: "#a1a1aa", label: "N/A" }
    : { bg: "var(--surface-2)", border: "var(--border)", color: "#e5e7eb", label: "" };

  // Show whatever numeric / textual value the tradesperson actually recorded
  // (SMU, grouser height, track sag, pin-matrix average, selector choice, etc.)
  // so the audit trail is not just "OK" — it reads "165 mm · OK", "5678 hrs · OK".
  const unit = item?.unit || "";
  const kind = item?.kind || "check";
  const numericValue = state?.value;
  const selectorValue = state?.value_str;
  const pinAvg = state?.extras?.average;
  const valueText = (() => {
    if (isWearPct) return null; // already shown as the % pill
    if (kind === "measurement" && numericValue !== null && numericValue !== undefined && numericValue !== "") {
      return `${numericValue}${unit ? " " + unit : ""}`;
    }
    if (kind === "pin_matrix" && pinAvg != null) {
      return `AVG ${Number(pinAvg).toFixed(1)}${unit || "°C"}`;
    }
    if (kind === "selector" && (selectorValue || numericValue)) {
      return String(selectorValue || numericValue);
    }
    return null;
  })();

  return (
    <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
      {saving && (
        <span className="pill !text-[10px] !py-0.5 !px-2 shrink-0" title="Saving…" data-testid="task-saving">
          <Loader2 className="w-3 h-3 animate-spin" /> Saving
        </span>
      )}
      {who && (
        <span className="pill !text-[10px] !py-0.5 !px-2 shrink-0" title={who}>
          <Users className="w-3 h-3" /> {truncName(who)}
        </span>
      )}
      {when && (
        <span className="pill !text-[10px] !py-0.5 !px-2 shrink-0" title={new Date(when).toLocaleString()}>
          <Clock className="w-3 h-3" /> {fmtShort(when)}
        </span>
      )}
      {valueText && (
        <span
          className="pill !text-[10px] !py-0.5 !px-2 shrink-0 font-black uppercase tracking-widest tabular-nums"
          style={{ background: "#4a2a06", border: "1px solid #f58220", color: "#ffb347" }}
          data-testid={`task-value${item?.id ? "-" + item.id : ""}`}
          title="Value recorded on the inspection"
        >
          {valueText}
        </span>
      )}
      {tone.label && (
        <span className="pill !text-[10px] !py-0.5 !px-2 shrink-0 font-black uppercase tracking-widest tabular-nums"
              style={{ background: tone.bg, border: `1px solid ${tone.border}`, color: tone.color }}>
          {tone.label}
        </span>
      )}
    </div>
  );
}

/* ---------------- utils ---------------- */
function canDeleteInspection(user /* , insp */) {
  if (!user) return false;
  if (user.is_admin) return true;
  const role = String(user.role || "").toLowerCase();
  // Any supervisor-level role can delete. We deliberately DON'T block on
  // terminal status here — the confirm modal is what protects against
  // accidental deletion, not the button visibility.
  return /admin|control|owner|supervisor|superintendent|planner/.test(role);
}

function ConfirmDeleteInspection({ asset, template, busy, onCancel, onConfirm }) {
  return (
    <div
      className="fixed inset-0 z-[65] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)" }}
      onClick={onCancel}
      data-testid="delete-inspection-confirm"
    >
      <div
        className="panel-raised w-full max-w-md p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: "rgba(220,38,38,0.18)", color: "#ff9b9b", border: "1px solid rgba(255,120,120,0.35)" }}
          >
            <Trash2 className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-muted">Delete inspection</div>
            <div className="font-display font-black text-xl leading-tight mt-1 break-words">
              Delete inspection for {asset}?
            </div>
            {template && <div className="text-sm text-muted mt-1 truncate">{template}</div>}
            <div className="text-sm text-white/80 mt-3 leading-relaxed">
              This removes the inspection and every response your crew has recorded on it. This cannot be undone.
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2" style={{ borderTop: "1px solid var(--border)" }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={onCancel}
            disabled={busy}
            data-testid="delete-inspection-cancel"
          >
            Cancel
          </button>
          <button
            className="btn btn-sm"
            onClick={onConfirm}
            disabled={busy}
            data-testid="delete-inspection-confirm-btn"
            style={{
              background: "linear-gradient(180deg, rgba(220,38,38,0.95), rgba(185,28,28,0.95))",
              border: "1px solid rgba(255,120,120,0.5)",
              color: "#fff",
              boxShadow: "0 6px 18px rgba(220,38,38,0.35)",
            }}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Delete inspection
          </button>
        </div>
      </div>
    </div>
  );
}

function flattenItems(insp) {
  if (!insp) return [];
  const out = [];
  (insp.sections || []).forEach((s) => (s.items || []).forEach((it) => out.push(it)));
  if (!out.length && Array.isArray(insp.items)) return insp.items;
  return out;
}
function fmtShort(v) {
  try {
    const d = new Date(v);
    return d.toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return String(v || ""); }
}
function truncName(n) { const s = String(n || ""); return s.length > 14 ? s.slice(0, 14) + "…" : s; }

/**
 * pickUrl — extract an image URL from any of the shapes the mobile app
 * or backend may return (string URL, data-URL, or a wrapper object).
 * Accepts every known photo/annotation key so the technician's markups
 * (which come through as objects rather than plain strings) are never
 * silently dropped.
 *
 * The mobile app persists markups as
 *   { id, base_image_uri, overlay_svg }
 * so `base_image_uri` MUST be recognised as the base photo URL. The
 * accompanying SVG (drawn strokes / circled damage) is composited by
 * <MarkupImage/> — see mergePhotosAndMarkups + TaskCard below.
 */
function pickUrl(p) {
  if (!p) return null;
  const raw = typeof p === "string" ? p : (
    p.base_image_uri ||
    p.annotated_url ||
    p.marked_up_url ||
    p.annotated ||
    p.markup_url ||
    p.overlay_url ||
    p.image_url ||
    p.image ||
    p.url ||
    p.data ||
    p.photo_url ||
    p.href ||
    p.src ||
    null
  );
  if (!raw || typeof raw !== "string") return raw;
  // Backend serves image references as relative "/api/photos/{pid}" paths;
  // resolve them against the production API host so the portal preview
  // domain doesn't 404 trying to fetch them from itself.
  if (/^(https?:|data:|blob:)/i.test(raw)) return raw;
  if (raw.startsWith("/")) return `${API_BASE}${raw}`;
  return raw;
}

/**
 * pickOverlaySvg — pull the SVG markup string out of a markup object if
 * the mobile app included one. Returns null for plain photos.
 */
function pickOverlaySvg(p) {
  if (!p || typeof p !== "object") return null;
  return p.overlay_svg || p.svg || p.markup_svg || null;
}

/**
 * mergePhotosAndMarkups — combine `state.photos` and `state.markups` into a
 * single tagged list so the supervisor can see BOTH the raw photo and the
 * annotated (circled/highlighted) version the tradesperson drew on the
 * mobile app. Markup entries are shown FIRST because they carry the fault
 * evidence supervisors are looking for. Each entry is tagged so the UI can
 * badge the annotated tiles distinctly.
 *
 * Returns: [{ url, svg, annotated }]
 *   - url:       required, the base image URL
 *   - svg:       optional SVG string overlay (rendered over url)
 *   - annotated: boolean flag driving the "MARKED" chip in the UI
 */
function mergePhotosAndMarkups(photos, markups) {
  const out = [];
  const seen = new Set();
  const pushIfNew = (url, svg, annotated) => {
    if (!url) return;
    const key = url + "|" + (svg ? svg.length : 0);
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ url, svg: svg || null, annotated });
  };
  (Array.isArray(markups) ? markups : []).forEach((m) => pushIfNew(pickUrl(m), pickOverlaySvg(m), true));
  (Array.isArray(photos) ? photos : []).forEach((p) => {
    const url = pickUrl(p);
    // Dedupe against a markup that references the same base image so we
    // don't show the same photo twice (once with overlay, once without).
    const alreadyMarked = out.some((x) => x.url === url && x.svg);
    if (alreadyMarked) return;
    pushIfNew(url, pickOverlaySvg(p), false);
  });
  return out;
}

/**
 * MarkupImage — renders a base photo with an SVG overlay (circled damage,
 * arrows, freehand strokes) drawn by the technician in the mobile app.
 *
 * The overlay SVG comes with `viewBox` matching the photo's native pixel
 * dimensions and `preserveAspectRatio="xMidYMid meet"`, so we can fit both
 * the img and the SVG to the same box using `object-contain` and they
 * will align pixel-perfect regardless of container size.
 *
 * We render the SVG through `dangerouslySetInnerHTML` because the source
 * is our own trusted backend (customer-assets.emergentagent.com). If that
 * ever changes, sanitise `svg` with DOMPurify first.
 */
function MarkupImage({ url, svg, alt = "", className = "", imgClassName = "" }) {
  return (
    <div className={`relative ${className}`} style={{ background: "#0a0a0a" }}>
      <img
        src={url}
        alt={alt}
        className={`block w-full h-full object-contain ${imgClassName}`}
        loading="lazy"
      />
      {svg && (
        <div
          className="absolute inset-0 pointer-events-none flex items-center justify-center"
          // The SVG element inside carries its own viewBox +
          // preserveAspectRatio; giving the wrapper the same box as the
          // img means the overlay lines up regardless of tile size.
          dangerouslySetInnerHTML={{ __html: svg.replace(/<svg([^>]*)>/, '<svg$1 style="width:100%;height:100%;">') }}
        />
      )}
    </div>
  );
}

// Silence eslint about ChevronRight not being used (imported for future markup)
export const __unused = ChevronRight;

/**
 * Inline compact chip that shows a Work Order # and lets any capable user
 * type / clear it in place. Empty state shows "+ Add Work Order #".
 */
function WorkOrderChip({ value, onSave, disabled }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => setDraft(value || ""), [value]);

  const commit = async () => {
    const next = String(draft || "").trim();
    if (next === String(value || "").trim()) { setEditing(false); return; }
    setBusy(true);
    try { await onSave(next); }
    finally { setBusy(false); setEditing(false); }
  };
  const cancel = () => { setDraft(value || ""); setEditing(false); };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => !disabled && setEditing(true)}
        disabled={disabled}
        className="inline-flex items-center gap-1.5 rounded-full text-[11px] px-2 py-0.5 font-mono-tight transition hover:brightness-110"
        style={{
          background: value ? "rgba(245,130,32,0.14)" : "rgba(255,255,255,0.05)",
          border: `1px solid ${value ? "rgba(245,130,32,0.35)" : "rgba(255,255,255,0.15)"}`,
          color: value ? "var(--primary)" : "#a1a1aa",
        }}
        title={disabled ? "Locked" : "Edit Work Order #"}
        data-testid="wo-chip"
      >
        <span className="uppercase tracking-widest text-[9px] font-black opacity-80">WO#</span>
        <span className="font-black">{value || "+ Add"}</span>
      </button>
    );
  }
  return (
    <div className="inline-flex items-center gap-1" data-testid="wo-chip-form">
      <span className="text-[9px] uppercase tracking-widest font-black text-muted">WO#</span>
      <input
        autoFocus
        className="input !min-h-[24px] !py-0.5 !px-2 !w-28 text-xs font-mono-tight"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") cancel();
        }}
        placeholder="e.g. WO-12345"
        maxLength={40}
        disabled={busy}
        data-testid="wo-chip-input"
      />
      <button type="button" onClick={commit} disabled={busy} className="btn btn-xs btn-primary !py-0.5 !px-1.5" data-testid="wo-chip-save" title="Save">
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
      </button>
      <button type="button" onClick={cancel} disabled={busy} className="btn btn-xs btn-ghost !py-0.5 !px-1.5" title="Cancel" data-testid="wo-chip-cancel">
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

/**
 * Inline compact chip that shows Service Meter Unit (SMU) hours. Editing
 * PATCHes the SMU measurement item's value — the same source of truth the
 * mobile app / PDF read from — so entering hours here auto-fills the SMU
 * task inside the inspection.
 */
function SmuChip({ value, unit = "hrs", onSave, disabled }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value === "" || value === null ? "" : String(value));
  const [busy, setBusy] = useState(false);

  useEffect(() => setDraft(value === "" || value === null ? "" : String(value)), [value]);

  const commit = async () => {
    const next = String(draft || "").trim();
    if (next === String(value ?? "").trim()) { setEditing(false); return; }
    if (next && !Number.isFinite(Number(next))) { setEditing(false); return; }
    setBusy(true);
    try { if (next) await onSave(next); }
    finally { setBusy(false); setEditing(false); }
  };
  const cancel = () => { setDraft(value === "" || value === null ? "" : String(value)); setEditing(false); };

  if (!editing) {
    const hasVal = value !== "" && value !== null && value !== undefined;
    return (
      <button
        type="button"
        onClick={() => !disabled && setEditing(true)}
        disabled={disabled}
        className="inline-flex items-center gap-1.5 rounded-full text-[11px] px-2 py-0.5 font-mono-tight transition hover:brightness-110"
        style={{
          background: hasVal ? "rgba(74,222,128,0.14)" : "rgba(255,255,255,0.05)",
          border: `1px solid ${hasVal ? "rgba(74,222,128,0.35)" : "rgba(255,255,255,0.15)"}`,
          color: hasVal ? "#4ade80" : "#a1a1aa",
        }}
        title={disabled ? "Locked" : "Enter machine hours — auto-fills the SMU task"}
        data-testid="smu-chip"
      >
        <span className="uppercase tracking-widest text-[9px] font-black opacity-80">SMU</span>
        <span className="font-black tabular-nums">
          {hasVal ? `${value} ${unit}` : "+ Add hrs"}
        </span>
      </button>
    );
  }
  return (
    <div className="inline-flex items-center gap-1" data-testid="smu-chip-form">
      <span className="text-[9px] uppercase tracking-widest font-black text-muted">SMU</span>
      <input
        autoFocus
        inputMode="decimal"
        type="number"
        step="0.1"
        className="input !min-h-[24px] !py-0.5 !px-2 !w-24 text-xs font-mono-tight tabular-nums"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") cancel();
        }}
        placeholder="0"
        disabled={busy}
        data-testid="smu-chip-input"
      />
      <span className="text-[10px] text-muted uppercase tracking-widest">{unit}</span>
      <button type="button" onClick={commit} disabled={busy} className="btn btn-xs btn-primary !py-0.5 !px-1.5" data-testid="smu-chip-save" title="Save">
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
      </button>
      <button type="button" onClick={cancel} disabled={busy} className="btn btn-xs btn-ghost !py-0.5 !px-1.5" title="Cancel" data-testid="smu-chip-cancel">
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}



/* ---------------- Raise-PPF panel ----------------------------------------
 * Shown inline under the OK/ISSUE/N/A row whenever the current item is
 * flagged as ISSUE. Lets the tradesperson raise a Planning Request Form
 * (PPF) in one tap so the fault "auto goes into the PPF section" (Josh's
 * exact spec). Once raised, we record the returned PPF id in the item's
 * `extras.ppf_id` so we don't spawn duplicates on subsequent renders.
 * -------------------------------------------------------------------------- */
function RaisePpfPanel({ item, state, hasPpf, inspection, onSaveExtras }) {
  const [open, setOpen] = useState(false);
  const [priority, setPriority] = useState(state?.extras?.ppf_priority || "medium");
  const [fault, setFault] = useState(state?.extras?.ppf_fault || item.label || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [raisedRef, setRaisedRef] = useState(state?.extras?.ppf_ref || null);

  const priorities = [
    { code: "critical", label: "Critical", color: "#ff5252" },
    { code: "high",     label: "High",     color: "#ffb347" },
    { code: "medium",   label: "Medium",   color: "#facc15" },
    { code: "low",      label: "Low",      color: "#4ade80" },
  ];

  const raise = async () => {
    setError(null);
    setBusy(true);
    try {
      const payload = {
        asset_id: inspection?.asset_id || null,
        fault: String(fault || item.label || "").trim(),
        priority,
        source: "inspection",
        source_inspection_id: inspection?.id || inspection?._id || null,
        source_item_id: item.id,
        source_item_label: item.label || null,
      };
      const { data } = await api.post("/ppfs", payload);
      const ppfId = data?.id || data?._id || data?.ppf_id || null;
      const ppfRef = data?.work_order_number || data?.reference || (ppfId ? String(ppfId).slice(0, 8) : "raised");
      setRaisedRef(ppfRef);
      onSaveExtras?.({
        ppf_id: ppfId,
        ppf_ref: ppfRef,
        ppf_priority: priority,
        ppf_fault: payload.fault,
        ppf_raised_at: new Date().toISOString(),
      });
      setOpen(false);
    } catch (ex) {
      setError(apiError(ex));
    } finally {
      setBusy(false);
    }
  };

  if (hasPpf || raisedRef) {
    return (
      <div className="mt-3 rounded-lg px-3 py-2 flex items-center gap-2"
           style={{ background: "rgba(74,222,128,0.10)", border: "1px solid rgba(74,222,128,0.35)" }}
           data-testid={`task-ppf-raised-${item.id}`}>
        <CheckCircle2 className="w-4 h-4" style={{ color: "#4ade80" }} />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-widest font-black" style={{ color: "#4ade80" }}>
            Planning Request raised · {raisedRef || state?.extras?.ppf_ref || "#" + String(state?.extras?.ppf_id || "").slice(0, 8)}
          </div>
          <div className="text-[10px] text-muted truncate">
            {state?.extras?.ppf_priority && <>Priority: <span className="uppercase">{state.extras.ppf_priority}</span> · </>}
            Finish this off from the Planning Request page after the inspection is complete.
          </div>
        </div>
        <a
          href="/ppfs"
          target="_blank"
          rel="noreferrer"
          className="btn btn-xs btn-ghost"
          title="Open Planning Request page in a new tab"
          data-testid={`task-ppf-open-${item.id}`}
        >
          <ExternalLink className="w-3 h-3" /> Open
        </a>
      </div>
    );
  }

  if (!open) {
    return (
      <div className="mt-3">
        <button
          type="button"
          className="btn btn-sm"
          style={{
            background: "linear-gradient(180deg, rgba(245,130,32,0.15), rgba(245,130,32,0.05))",
            border: "1px solid rgba(245,130,32,0.4)",
            color: "var(--primary)",
          }}
          onClick={() => setOpen(true)}
          data-testid={`task-raise-ppf-${item.id}`}
        >
          <Zap className="w-4 h-4" /> Raise Planning Request for this issue
        </button>
        <span className="text-[10px] text-muted ml-2">
          Auto-lands in the Planning Request section — finish it off after the inspection.
        </span>
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-lg p-3 space-y-2"
         style={{ background: "rgba(245,130,32,0.06)", border: "1px solid rgba(245,130,32,0.35)" }}
         data-testid={`task-raise-ppf-form-${item.id}`}>
      <div className="flex items-center gap-2">
        <Zap className="w-4 h-4" style={{ color: "var(--primary)" }} />
        <div className="text-[11px] uppercase tracking-widest font-black" style={{ color: "var(--primary)" }}>
          Raise Planning Request
        </div>
        <button
          type="button"
          className="ml-auto btn btn-xs btn-ghost !py-0.5"
          onClick={() => { setOpen(false); setError(null); }}
          data-testid={`task-raise-ppf-cancel-${item.id}`}
        >
          <X className="w-3 h-3" />
        </button>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted font-black mb-1">Priority *</div>
        <div className="flex flex-wrap gap-1.5" data-testid={`task-raise-ppf-priority-${item.id}`}>
          {priorities.map((p) => {
            const on = priority === p.code;
            return (
              <button
                key={p.code}
                type="button"
                onClick={() => setPriority(p.code)}
                className="text-[10.5px] uppercase tracking-widest font-black px-2 py-1 rounded-md"
                style={{
                  background: on ? p.color : `${p.color}22`,
                  color: on ? "#0a0a0a" : p.color,
                  border: `1px solid ${p.color}${on ? "" : "55"}`,
                  boxShadow: on ? `0 0 0 2px ${p.color}33` : "none",
                }}
                data-testid={`task-raise-ppf-priority-${p.code}-${item.id}`}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted font-black mb-1">Fault description</div>
        <textarea
          className="input !py-2 !text-[13px]"
          rows={2}
          value={fault}
          onChange={(e) => setFault(e.target.value)}
          placeholder="Describe the fault so the planner has enough context"
          data-testid={`task-raise-ppf-fault-${item.id}`}
        />
      </div>
      {error && (
        <div className="text-[11px] rounded px-2 py-1"
             style={{ background: "rgba(220,38,38,0.14)", color: "#ff9b9b", border: "1px solid rgba(220,38,38,0.35)" }}>
          {error}
        </div>
      )}
      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          onClick={() => setOpen(false)}
        >
          Skip
        </button>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          disabled={busy || !fault.trim()}
          onClick={raise}
          data-testid={`task-raise-ppf-submit-${item.id}`}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
          Raise Planning Request
        </button>
      </div>
    </div>
  );
}

/* ---------------- LiveSaveStatus ----------------------------------------
 * Small header pill mirroring the mobile app's auto-save behaviour so
 * tradespeople on the portal see the exact same "your work is safe" cue as
 * on-tablet — every OK/Issue/N/A tap or numeric entry PATCHes the shared DB
 * instantly (no explicit "Save Draft" button needed).
 * -------------------------------------------------------------------------- */
function LiveSaveStatus({ isFinalised, isSubmitted, savingCount, readOnly }) {
  let state, label, tone, Icon;
  if (readOnly) { state = "readOnly"; label = "View only · past swing"; tone = { bg: "rgba(148,163,184,0.14)", fg: "#cbd5e1", brd: "rgba(148,163,184,0.35)" }; Icon = ClipboardCheck; }
  else if (isFinalised) { state = "locked"; label = "Approved · locked"; tone = { bg: "rgba(74,222,128,0.14)", fg: "#4ade80", brd: "rgba(74,222,128,0.35)" }; Icon = CheckCircle2; }
  else if (savingCount > 0) { state = "saving"; label = savingCount === 1 ? "Saving 1 field…" : `Saving ${savingCount} fields…`; tone = { bg: "rgba(245,130,32,0.16)", fg: "var(--primary)", brd: "rgba(245,130,32,0.4)" }; Icon = Loader2; }
  else if (isSubmitted) { state = "submitted"; label = "Submitted · awaiting sign-off"; tone = { bg: "rgba(245,130,32,0.14)", fg: "var(--primary)", brd: "rgba(245,130,32,0.35)" }; Icon = Send; }
  else { state = "draft"; label = "Draft · auto-saved to shared DB"; tone = { bg: "rgba(74,222,128,0.12)", fg: "#4ade80", brd: "rgba(74,222,128,0.30)" }; Icon = Check; }
  return (
    <div
      className="mt-2 inline-flex items-center gap-1.5 pill !text-[10px] !py-1 !px-2 uppercase font-black tracking-widest"
      style={{ background: tone.bg, color: tone.fg, border: `1px solid ${tone.brd}` }}
      data-testid="inspection-live-save-status"
      data-state={state}
      title={state === "draft" ? "Every field you fill in here is written to the same production database the mobile app uses — no separate save button needed." : label}
    >
      <Icon className={`w-3 h-3 ${state === "saving" ? "animate-spin" : ""}`} />
      {label}
    </div>
  );
}
